/**
 * Launcher V2 — zod schemas for every Json column plus the `/v2` wire shapes.
 *
 * PR A1, Unit U1. Plan: plans/2026-08-27-002-launcher-v2-a1-foundation-impl-plan.md.
 *
 * Every Json column on the eight `launcher_*` V2 tables gets its schema here,
 * and every read of those columns goes through `parseDbJson()` (see
 * docs/v4-system/db-json-parsing.md) — so this module is the single place the
 * shape of stored launcher content is defined. TS types are derived with
 * `z.infer` only; never hand-write a parallel interface (enforced by
 * `terra/no-parallel-schema-and-type`).
 */
import { z } from 'zod';

// The canonical Flow filter schemas, reached through the `_shared` re-export —
// mini-apps never import from `../../src` directly. Reusing them is deliberate:
// a fourth filter implementation is explicitly forbidden by the playbooks
// architecture doc.
import { FilterGroupSchema, GroupLogicSchema } from '../_shared/flow/filter';
import { RepDispatchStatusSchema } from '../_shared/clay-run-status-schema';
import { ChannelSchema, type Channel } from '../_shared/launcher-channel-schema';
// TYPE-ONLY, and deliberately so: `_shared/launcher-copy-tokens.ts` is
// frontend-safe and zod-free (every `_shared/**` change triggers the
// core-review workflow), so its `EmptyFallback` union cannot carry its own
// schema. `EmptyFallbackSchema` below is its zod MIRROR, pinned to it at
// compile time — see that schema's doc comment.
import type { AppliedFallback, EmptyFallback, ValueDefect } from '../_shared/launcher-copy-tokens';
// TYPE-ONLY as well (erased at runtime, so this adds no import edge): U2's
// classifier is pure and browser-safe, and `TokenFlagKindSchema` below is its
// mirror on the wire.
import type { NonBraceDefectKind, TokenFlagKind } from './sequence-token-flags';

// ─────────────────────────────────────────────────────────────────────────────
// playbooks_segmentation_version.conditions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A segmentation's audience definition: two-level AND/OR filter groups, exactly
 * the envelope every other Flow table surface stores (`groups` + the outer
 * `groupLogic` joining them).
 *
 * An empty `groups` array is legal — a segmentation can be saved mid-draft
 * before it matches anybody.
 */
export const SegmentationConditionsSchema = z.object({
  groups: z.array(FilterGroupSchema),
  groupLogic: GroupLogicSchema,
});
export type SegmentationConditions = z.infer<typeof SegmentationConditionsSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Mapping — shared by playbooks_segmentation_version.default_mapping and each
// playbooks_playbook_version rung
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The closed 3-channel vocabulary, unchanged from v1 dispatch. Stored as a
 * plain text column on `playbooks_sequence.channel`; validated here.
 */
export const LauncherChannelSchema = ChannelSchema;
export type LauncherChannel = Channel;

/**
 * "Send THIS sequence, THIS way." The one shape both a segmentation's
 * `default_mapping` and a playbook rung's `mapping` use.
 *
 * A mapping always renders its sequence's current head — there is no version
 * pin here (the pin concept, and its one writer, branch-edit, were removed —
 * see plans/2026-09-02-001-launcher-v2-rip-out-versioning-plan.md). Diverging
 * one rung's copy from the rest is done by explicitly duplicating the
 * sequence (`POST /v2/sequences/:id/duplicate`) and remapping the rung, not by
 * pinning a version.
 *
 * `sender` is a free-form string rather than an enum: the vocabulary is the
 * inline sender popover's (`gtme_owner_email` | `gs_owner_email` |
 * `claydr_email` | a static email address) and PR C owns it. Enumerating it
 * here would make every future sender token a `db_json_schema_mismatch`.
 * Empty (`''`) is legal (QA-fixes E2c, plan Decision 10) — a mapping's
 * sequence and its sender are set in two different places now, so a
 * freshly-mapped rung has a real, representable gap rather than a forced
 * placeholder value. `sequence-readiness.ts` treats it as `'not_ready'`.
 */
export const SequenceMappingSchema = z.object({
  sequenceId: z.string().min(1),
  channel: LauncherChannelSchema,
  // QA-fixes E2c (plan Decision 10) — relaxed from `.min(1)`. Sender is no
  // longer collected by the "Change sequence" drawer (it's set afterward via
  // `InlineSenderPopover`, already sitting next to the rest of a sequence's
  // setup), so a freshly-mapped rung legally has none yet.
  // `sequence-readiness.ts` already treats an empty string as `'not_ready'`.
  sender: z.string(),
  /// Which identity's booking link to embed (Mapping-level per the round-8
  /// object model). Optional: not every channel/template uses one.
  bookingLinkIdentity: z.string().min(1).optional(),
  /// UNUSED (2026-09-17, playbooks-inflection-wiring): nothing writes these and
  /// the send path never reads them. The LIVE Inflection CC / Reply-to are
  /// identity-level (`playbooks_sequence.default_cc` / `default_reply_to`,
  /// surfaced as `PlaybooksSequenceSummary.cc` / `.replyTo`), for the same
  /// reason sender moved to the identity (Decision 3). Kept only so an
  /// already-persisted mapping carrying them still parses; deleting them is a
  /// candidate follow-up, not done here.
  cc: z.array(z.string().min(1)).optional(),
  replyTo: z.string().min(1).optional(),
});
export type SequenceMapping = z.infer<typeof SequenceMappingSchema>;

/**
 * `playbooks_segmentation_version.default_mapping` — a mapping, or NULL.
 *
 * **NULL is suppression.** There is no `kind` column, no suppression badge
 * vocabulary, and no separate suppression primitive anywhere in V2 (phases-doc
 * Decision 13): "a suppression" just means a segmentation with no mapped
 * sequence.
 */
export const SegmentationDefaultMappingSchema = SequenceMappingSchema.nullable();
export type SegmentationDefaultMapping = z.infer<typeof SegmentationDefaultMappingSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// playbooks_sequence_version.steps
// ─────────────────────────────────────────────────────────────────────────────

/** Copy-readiness, same two-value vocabulary as v1's `copy_status`. */
export const SequenceCopyStatusSchema = z.enum(['draft', 'ready']);
export type SequenceCopyStatus = z.infer<typeof SequenceCopyStatusSchema>;

/**
 * One step of a sequence version's self-contained snapshot.
 *
 * A1 types what it owns — position and copy — and snapshots the channel's own
 * step descriptor OPAQUELY (`z.record(z.string(), z.unknown())`, Key Decision
 * 12). PR C owns the descriptor's real shape and will keep changing it; typing
 * it here would turn every future descriptor field into a
 * `db_json_schema_mismatch` and a null read. A record (unlike an object) keeps
 * unknown keys instead of stripping them, which is what makes the snapshot
 * genuinely lossless and lets Phase 2 diff two Gong-backed versions.
 */
export const SequenceStepSchema = z.object({
  position: z.number().int().nonnegative(),
  subject: z.string().optional(),
  body: z.string().optional(),
  copyStatus: SequenceCopyStatusSchema.optional(),
  gongStepDescriptor: z.record(z.string(), z.unknown()).optional(),
});
export type SequenceStep = z.infer<typeof SequenceStepSchema>;

/**
 * `playbooks_sequence_version.steps` — the FULL step structure at save time.
 *
 * Empty is legal (a sequence identity can exist before any copy is written).
 * Positions must be unique: the snapshot is an ordered structure and a repeated
 * position makes the order ambiguous for every downstream consumer (diffing,
 * send, the copy-history click-through).
 */
export const SequenceStepsSchema = z.array(SequenceStepSchema).superRefine((steps, ctx) => {
  const seen = new Set<number>();
  steps.forEach((step, index) => {
    if (seen.has(step.position)) {
      ctx.addIssue({
        code: 'custom',
        path: [index, 'position'],
        message: `Duplicate step position ${step.position} — step positions must be unique.`,
      });
    }
    seen.add(step.position);
  });
});
export type SequenceSteps = z.infer<typeof SequenceStepsSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// playbooks_playbook_version.rungs
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One rung of the first-match-wins ladder.
 *
 * `id` is the rung's STABLE IDENTITY and `position` is only its ORDER. The two
 * are deliberately separate: a reorder renumbers every `position` (and a
 * `segmentationId` can legitimately repeat across rungs — the seed's catch-all
 * borrows an earlier rung's segmentation), so neither one, nor the pair, can
 * address "this rung" across a write. `id` never changes once minted, so
 * anything pointing at a rung — the Review approvals map, a staged draft edit,
 * a per-rung matched-members query — keys off `id`. It is copied verbatim by
 * duplicate-playbook and by fork, because uniqueness is a PER-LADDER contract
 * (enforced in `PlaybookRungsSchema` below), not a global one.
 *
 * Minting is server-side and lives in the playbook write path: PR C U1 ships
 * the field, the backfill migration and the seed's ids; the `savePlaybook`
 * endpoint that mints `crypto.randomUUID()` for a newly-added rung — and never
 * re-mints one for a rung that already carries an id — arrives with U3. The
 * consumers still keyed off `position`/`segmentationId` migrate onto `id` in
 * U2.
 *
 * A rung always renders its segmentation's current head — there is no version
 * pin here (removed along with the mapping's — see
 * plans/2026-09-02-001-launcher-v2-rip-out-versioning-plan.md). `mapping:
 * null` is the suppression case — required-but-nullable so "no sequence" is
 * always a recorded decision rather than a missing field.
 */
export const PlaybookRungSchema = z.object({
  id: z.string().min(1),
  position: z.number().int().nonnegative(),
  segmentationId: z.string().min(1),
  mapping: SequenceMappingSchema.nullable(),
  isCatchAll: z.boolean(),
});
export type PlaybookRung = z.infer<typeof PlaybookRungSchema>;

/**
 * `playbooks_playbook_version.rungs` — the ordered ladder.
 *
 * Four invariants, enforced here and re-checked in `createVersion()` (the
 * single write path) rather than by the DB:
 *
 *  1. **Ids are unique within the ladder** — `id` is how every consumer
 *     addresses a rung, so a repeat makes the reference ambiguous. Scoped to
 *     the ladder ON PURPOSE: duplicate-playbook copies rung ids verbatim, and
 *     no consumer needs cross-playbook uniqueness (a rung reference always
 *     arrives alongside its `playbookId`).
 *  2. **Positions are unique** — a repeated position makes the ladder
 *     ambiguous.
 *  3. **Positions ascend with array order** — evaluation is first-match-wins on
 *     the array, so a position sequence that disagrees with the array order
 *     would mislead every reader (and any consumer that sorts by position would
 *     evaluate a different ladder than one that iterates).
 *  4. **A catch-all is last, and there is at most one** — a catch-all matches
 *     everybody, so anything below it is unreachable.
 *
 * An empty ladder is legal; a catch-all is not *required* by the schema
 * (playbook build-time validation in PR C owns "must end in a catch-all",
 * which needs to tolerate intermediate editing states).
 */
export const PlaybookRungsSchema = z.array(PlaybookRungSchema).superRefine((rungs, ctx) => {
  const seenIds = new Set<string>();
  const seen = new Set<number>();
  let previousPosition: number | null = null;

  rungs.forEach((rung, index) => {
    if (seenIds.has(rung.id)) {
      ctx.addIssue({
        code: 'custom',
        path: [index, 'id'],
        message: `Duplicate rung id ${rung.id} — rung ids must be unique within a playbook's ladder.`,
      });
    }
    seenIds.add(rung.id);

    if (seen.has(rung.position)) {
      ctx.addIssue({
        code: 'custom',
        path: [index, 'position'],
        message: `Duplicate rung position ${rung.position} — rung positions must be unique.`,
      });
    }
    seen.add(rung.position);

    if (previousPosition !== null && rung.position <= previousPosition) {
      ctx.addIssue({
        code: 'custom',
        path: [index, 'position'],
        message: `Rung positions must ascend with array order (position ${rung.position} follows ${previousPosition}) — the array order is the first-match-wins order.`,
      });
    }
    previousPosition = rung.position;

    if (rung.isCatchAll && index !== rungs.length - 1) {
      ctx.addIssue({
        code: 'custom',
        path: [index, 'isCatchAll'],
        message: 'The catch-all rung must be last — rungs below a catch-all are unreachable.',
      });
    }
  });

  const catchAllCount = rungs.filter((rung) => rung.isCatchAll).length;
  if (catchAllCount > 1) {
    ctx.addIssue({
      code: 'custom',
      path: ['isCatchAll'],
      message: `A playbook has at most one catch-all rung (found ${catchAllCount}).`,
    });
  }
});
export type PlaybookRungs = z.infer<typeof PlaybookRungsSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// playbooks_play_send.resolved_refs
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A mapping with every reference concrete — the send-time form of
 * `SequenceMappingSchema`. Derived with `.extend()` rather than redeclared so
 * the two shapes cannot drift. `sequenceVersionId` is an ADD, not an override:
 * `SequenceMappingSchema` carries no version reference at all (a mapping
 * always renders its sequence's head), so this is where a concrete id first
 * gets attached — at the send instant, by `planResolveForSend`.
 */
export const ResolvedSequenceMappingSchema = SequenceMappingSchema.extend({
  sequenceVersionId: z.string().min(1),
});
export type ResolvedSequenceMapping = z.infer<typeof ResolvedSequenceMappingSchema>;

/**
 * One rung of the send lock. Same shape as a playbook rung except EVERY id is
 * concrete: nulls were resolved to the identities' heads at the send instant.
 *
 * `sequenceId` / `sequenceVersionId` are optional at the rung level because a
 * suppression rung has no sequence at all — but when present they must be
 * concrete strings, never null.
 */
export const ResolvedRefSchema = z.object({
  position: z.number().int().nonnegative(),
  segmentationId: z.string().min(1),
  segmentationVersionId: z.string().min(1),
  sequenceId: z.string().min(1).optional(),
  sequenceVersionId: z.string().min(1).optional(),
  mapping: ResolvedSequenceMappingSchema.nullable(),
  isCatchAll: z.boolean(),
});
export type ResolvedRef = z.infer<typeof ResolvedRefSchema>;

/**
 * `playbooks_play_send.resolved_refs` — the FINE half of the dual-granularity
 * lock-at-send record (the coarse half is the row's own concrete
 * `playbook_version_id` column). Written by PR D; defined now because this
 * shape IS the lock-at-send contract, and both Phase 2 roll-forward
 * granularities read it.
 */
export const ResolvedRefsSchema = z.array(ResolvedRefSchema);
export type ResolvedRefs = z.infer<typeof ResolvedRefsSchema>;

/**
 * `playbooks_play_send.identity_snapshot` (second review-fix pass U1, C1-1) —
 * the version-invariant identity facts the rep's approval was checked against,
 * frozen with the send row: for every segmentation the refs name `{ id, name }`,
 * for every sequence its name, channel and DELIVERY settings (`flow_id`, the
 * Inflection CC and Reply-to). The background build and the "what was posted"
 * preview read this instead of the live rows, so a Library edit mid-send can
 * never move later members to a different flow or CC. `null` only on rows
 * written before 2026-10-15.
 */
export const SendIdentitySnapshotSchema = z.object({
  segmentations: z.array(z.object({ id: z.string().min(1), name: z.string() })),
  sequences: z.array(
    z.object({
      id: z.string().min(1),
      name: z.string(),
      channel: z.string(),
      flow_id: z.string().nullable(),
      default_cc: z.string().nullable(),
      default_reply_to: z.string().nullable(),
    }),
  ),
});
export type SendIdentitySnapshot = z.infer<typeof SendIdentitySnapshotSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// `/v2` wire schemas — the read contract A2 (and the curl QA) consume
//
// Every name here is `Playbooks`-prefixed: these are launcher-V2-specific wire
// shapes and generic names like `PlaySummarySchema` would collide across the
// repo (`terra/no-duplicate-schema-name` + scripts/check-duplicate-schemas.ts).
// Timestamps are ISO-8601 strings — these shapes describe JSON on the wire,
// after `.toISOString()`, not Prisma rows.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One entry of an object's version history — the shape both the AI-copy history
 * click-through (PR C) and the divergence tree (PR B/A2) read.
 *
 * `isBranch` is `branch_play_id !== null`, surfaced as a boolean so the client
 * never needs to know about play ids to render main-line vs branch. Main-line
 * listings omit branch entries entirely; the field exists because the usage and
 * divergence views do show them.
 */
export const PlaybooksVersionHistoryEntrySchema = z.object({
  versionId: z.string().min(1),
  versionNumber: z.number().int().nullable(),
  parentVersionId: z.string().min(1).nullable(),
  mergeParentVersionId: z.string().min(1).nullable(),
  forkSourceVersionId: z.string().min(1).nullable(),
  isBranch: z.boolean(),
  changeSummary: z.string().nullable(),
  createdByEmail: z.string().nullable(),
  createdAt: z.string().min(1),
});
export type PlaybooksVersionHistoryEntry = z.infer<typeof PlaybooksVersionHistoryEntrySchema>;

/** Fields every library summary carries. */
const playbooksIdentitySummaryFields = {
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable(),
  headVersionId: z.string().min(1).nullable(),
  headVersionNumber: z.number().int().nullable(),
  createdByEmail: z.string().nullable(),
  updatedAt: z.string().min(1),
};

/**
 * Plan 2026-09-27-006 U3.7 (Key Technical Decision 9, Q9), rewritten by plan
 * 2026-09-28-006 U1 (rulebook R1/R3/R18) — who owns a segmentation /
 * sequence, on every summary. Nullable-not-optional: an emitter that forgets
 * them is a server bug, not a default to fill in.
 *
 * `owningPlayId` — `owning_play_id`: `null` for a Library identity, else the
 * play that owns this copy or play-created original.
 *
 * `forkSourceId` — the ROOT Library identity this row is a copy of
 * (`fork-roots.ts#ownedForkSourcesByCopy`; a copy of a copy still names the
 * Library root). Several copies may share one source — a play may hold one
 * copy per group. Computed only on the play-scoped library read
 * (`GET /library?playId=`), where the picker lists the Library row and each
 * copy as its own row; `null` for a Library row, a play-created original, and
 * on every unscoped read.
 *
 * `archivedAt` — ISO timestamp, non-null ONLY on the play-scoped library read,
 * for a Library piece an admin archived that this play's ladder still
 * references (shown "Archived in Library", never choosable, never offered
 * "update the playbook"). `null` for every choosable row and on every other
 * read.
 */
const playbooksOwnershipSummaryFields = {
  owningPlayId: z.string().nullable(),
  forkSourceId: z.string().nullable(),
  archivedAt: z.string().nullable(),
};

/**
 * `isMapped` is `default_mapping !== null` on the head version. Reported as a
 * plain boolean rather than a "suppression" label — there is no suppression
 * vocabulary (phases-doc Decision 13).
 *
 * `usageCount` — rungs across non-archived playbooks' head ladders that
 * reference this identity — template rungs only (the detail's
 * `activeMappingCount` additionally counts play-only places; see
 * `PlaybooksSequenceDetailResponseSchema`'s doc comment for the exclusions).
 * Required, not defaulted (round-3-QA-fixes plan, decision 11): a computed
 * zero from `getLibrary`'s usage-map pass is a real answer, not a fallback.
 *
 * `conditions` — the head version's audience definition (Playbooks QA round
 * 4, U3/S3), carried on the library payload so the "Add segmentation" picker
 * can describe each entry's filters without a second round trip. `null` for
 * a head-less identity or a drifted/unparseable JSON column (`getLibrary`
 * degrades via `parseDbJson` rather than throwing) — required, not
 * `.optional()`, for the same "a computed answer is not a fallback" reason
 * as `usageCount` above.
 */
export const PlaybooksSegmentationSummarySchema = z.object({
  ...playbooksIdentitySummaryFields,
  ...playbooksOwnershipSummaryFields,
  isMapped: z.boolean(),
  usageCount: z.number().int().nonnegative(),
  conditions: SegmentationConditionsSchema.nullable(),
});
export type PlaybooksSegmentationSummary = z.infer<typeof PlaybooksSegmentationSummarySchema>;

/**
 * `usageCount` — rungs across non-archived playbooks' head ladders that map
 * this sequence — template rungs only (`activeMappingCount` adds play-only
 * places). See
 * `PlaybooksSegmentationSummarySchema`'s doc comment for the requiredness
 * rationale.
 */
export const PlaybooksSequenceSummarySchema = z.object({
  ...playbooksIdentitySummaryFields,
  ...playbooksOwnershipSummaryFields,
  channel: LauncherChannelSchema,
  flowId: z.string().min(1).nullable(),
  stepCount: z.number().int().nonnegative(),
  usageCount: z.number().int().nonnegative(),
  /**
   * Playbooks QA round 4, U9 (SN1) / Decision 3 — `playbooks_sequence
   * .default_sender`: the sender this sequence is LOCKED to, copied onto
   * `mapping.sender` when a rung maps it. `null` = not set (Decision 10 ships
   * no backfill, so every pre-U9 sequence reads null until someone edits it
   * in Config → Sequences).
   *
   * A free-form `z.string()` for the same reason `SequenceMappingSchema.sender`
   * is: the vocabulary (owner tokens | a literal address) lives in
   * `sender-choice.ts`, and enumerating it here would turn every future token
   * into a validation failure on already-persisted rows. Note this is the
   * sequence's DEFAULT; `mapping.sender` remains the single read for
   * readiness/preview/tokens/send (Decision 11 — copy-on-map, no live
   * inheritance).
   */
  sender: z.string().nullable(),
  /**
   * playbooks-inflection-wiring (2026-09-17) — `playbooks_sequence.default_cc`
   * / `default_reply_to`: Inflection-only, same vocabulary and same
   * "`null` = not set, never backfilled" contract as `sender`. Required, not
   * optional, for the same reason `sender` is: an emitter that forgets them is
   * a server bug, not a default to fill in. Always `null` on a non-Inflection
   * sequence (the PATCH endpoint refuses to set them there).
   */
  cc: z.string().nullable(),
  replyTo: z.string().nullable(),
});
export type PlaybooksSequenceSummary = z.infer<typeof PlaybooksSequenceSummarySchema>;

export const PlaybooksPlaybookSummarySchema = z.object({
  ...playbooksIdentitySummaryFields,
  rungCount: z.number().int().nonnegative(),
});
export type PlaybooksPlaybookSummary = z.infer<typeof PlaybooksPlaybookSummarySchema>;

/**
 * `GET /v2/library` — the whole library in one call. Archived identities and
 * branch rows are excluded by the endpoint, not by this schema.
 */
export const PlaybooksLibraryResponseSchema = z.object({
  segmentations: z.array(PlaybooksSegmentationSummarySchema),
  sequences: z.array(PlaybooksSequenceSummarySchema),
  playbooks: z.array(PlaybooksPlaybookSummarySchema),
});
export type PlaybooksLibraryResponse = z.infer<typeof PlaybooksLibraryResponseSchema>;

/**
 * `GET /v2/segmentations/:id` — identity + head content + main-line history.
 * `head` is null only while an identity exists with no version yet.
 */
/**
 * A single segmentation version's content — shared by `GET /v2/segmentations/:id`
 * (as `head`, nullable) and `GET /v2/segmentations/:id/versions/:versionId`
 * (as `version`, always present since a 404 short-circuits first). A branch
 * row is representable here too: `versionNumber` is null for one, exactly as
 * it is in the DB.
 */
export const PlaybooksSegmentationVersionSchema = z.object({
  versionId: z.string().min(1),
  versionNumber: z.number().int().nullable(),
  conditions: SegmentationConditionsSchema,
  defaultMapping: SegmentationDefaultMappingSchema,
});
export type PlaybooksSegmentationVersion = z.infer<typeof PlaybooksSegmentationVersionSchema>;

export const PlaybooksSegmentationDetailResponseSchema = z.object({
  segmentation: PlaybooksSegmentationSummarySchema,
  head: PlaybooksSegmentationVersionSchema.nullable(),
  history: z.array(PlaybooksVersionHistoryEntrySchema),
});
export type PlaybooksSegmentationDetailResponse = z.infer<
  typeof PlaybooksSegmentationDetailResponseSchema
>;

/** `GET /v2/segmentations/:id/versions/:versionId`. */
export const PlaybooksSegmentationVersionResponseSchema = z.object({
  version: PlaybooksSegmentationVersionSchema,
});
export type PlaybooksSegmentationVersionResponse = z.infer<
  typeof PlaybooksSegmentationVersionResponseSchema
>;

/**
 * `playbooks_sequence_version.generation_inputs` — the AI-generate-copy inputs
 * that produced a version's `steps`, or `null` on a version that was
 * hand-edited (never generated) or predates this column. Every field is
 * required and non-null when the object itself is present — a drifted/partial
 * object is rejected by the schema and `parseDbJson` returns the whole thing
 * as `null` rather than a partially-populated object (U4b/U8a §8).
 */
export const PlaybooksGenerationInputsSchema = z.object({
  campaignSfdcId: z.string(),
  eventName: z.string(),
  campaignDescription: z.string(),
  memberStatuses: z.array(z.string()),
  instruction: z.string(),
  sourceMaterial: z.string(),
  model: z.string(),
  strategy: z.string(),
  /** Null only when the generation had no prior version to diff against. */
  sourceHeadVersionId: z.string().nullable(),
  generatedAt: z.string(),
});
export type PlaybooksGenerationInputs = z.infer<typeof PlaybooksGenerationInputsSchema>;

/**
 * `playbooks_sequence.generation_preset` — the identity-level "last generation
 * inputs" a rep used for this sequence, minus the run-specific fields
 * (`generatedAt`, `model`, `strategy`, `sourceHeadVersionId`), so the
 * "Generate with AI" drawer can preselect them (U9). `lastInputs` is `null`
 * until a generation has ever happened for this sequence.
 */
export const PlaybooksGenerationPresetSchema = z.object({
  campaignSfdcId: z.string(),
  lastInputs: PlaybooksGenerationInputsSchema.omit({
    generatedAt: true,
    model: true,
    strategy: true,
    sourceHeadVersionId: true,
  }).nullable(),
});
export type PlaybooksGenerationPreset = z.infer<typeof PlaybooksGenerationPresetSchema>;

/** A single sequence version's content — see `PlaybooksSegmentationVersionSchema`. */
export const PlaybooksSequenceVersionSchema = z.object({
  versionId: z.string().min(1),
  versionNumber: z.number().int().nullable(),
  steps: SequenceStepsSchema,
  generationInputs: PlaybooksGenerationInputsSchema.nullable().optional(),
});
export type PlaybooksSequenceVersion = z.infer<typeof PlaybooksSequenceVersionSchema>;

/**
 * `GET /v2/sequences/:id`.
 *
 * `activeMappingCount` — how many PLACES use this sequence: every rung across
 * every NON-ARCHIVED playbook's head ladder that maps it, plus every play-only
 * place (a non-archived play's own pinned ladder still mapping it at a rung
 * the template no longer does) — `usage.ts#usageAxes(...).places.length`,
 * exactly `GET /sequences/:id/usage`'s `places.length` and the number
 * `POST /sequences/:id/versions` rechecks `confirmedMappingCount` against
 * (plan 2026-09-27-007 U2 — one rule, client and server). It is the fact the in-play copy editor needs
 * to pick its save route (D1, Priority 1 step 3): `> 1` means an edit to the
 * library head would be immediately visible on another rung/Play, so the edit
 * has to branch; `1` means there is nothing to leak into and a main-line save is
 * fine. Computed server-side because a client only ever holds ONE playbook's
 * ladder, and cross-playbook sharing is exactly the case that leaked.
 *
 * Deliberately excludes: branch playbook versions (a branch IS the isolation —
 * counting it would make every already-branched sequence look more shared than
 * it is), archived playbooks ("no new work on this object", so not a place an
 * edit can leak to), and segmentation `default_mapping`s (a template for rungs
 * yet to be created, not a rung that renders copy at send time).
 *
 * `activeUnsentPlayCount` — the SECOND, independent axis of the same
 * shared-save acknowledgment (round-4 QA fixes, U5/S1, Decision 6): how many
 * draft (not yet `launched`) plays would silently receive a write to this
 * sequence's head. `api.ts#countSequenceSharedSaveAxes` is the producer (plan 2026-09-27-006 U4:
 * a play counts iff its own pinned ladder maps the sequence), and
 * `POST /v2/sequences/:id/versions`'s `confirmedUnsentPlayCount` is the
 * consumer — a caller that already fetched this detail (the Gong
 * import/rebind paths in `gong-flow-picker-api.ts`) forwards THIS number
 * rather than leaving the server to default it to `1` and 409 with
 * `shared_mapping_conflict`. Counted per distinct PLAYBOOK, never per rung,
 * for the same reason `usage-gate.ts#distinctUnsentPlays` is: two rungs on
 * one playbook share that playbook's plays.
 */
export const PlaybooksSequenceDetailResponseSchema = z.object({
  sequence: PlaybooksSequenceSummarySchema,
  head: PlaybooksSequenceVersionSchema.nullable(),
  history: z.array(PlaybooksVersionHistoryEntrySchema),
  activeMappingCount: z.number().int().nonnegative(),
  activeUnsentPlayCount: z.number().int().nonnegative(),
  /** `playbooks_sequence.generation_preset`, `null` until a generation has ever happened. */
  generationPreset: PlaybooksGenerationPresetSchema.nullable().optional(),
});
export type PlaybooksSequenceDetailResponse = z.infer<
  typeof PlaybooksSequenceDetailResponseSchema
>;

/** `GET /v2/sequences/:id/versions/:versionId`. */
export const PlaybooksSequenceVersionResponseSchema = z.object({
  version: PlaybooksSequenceVersionSchema,
});
export type PlaybooksSequenceVersionResponse = z.infer<
  typeof PlaybooksSequenceVersionResponseSchema
>;

/**
 * A single playbook version's content — see `PlaybooksSegmentationVersionSchema`.
 * `rungs` reuses `PlaybookRungsSchema`, so a ladder that breaks the
 * catch-all-last / unique-ascending-position invariants cannot be served even
 * if it somehow reached the DB.
 */
export const PlaybooksPlaybookVersionSchema = z.object({
  versionId: z.string().min(1),
  versionNumber: z.number().int().nullable(),
  rungs: PlaybookRungsSchema,
});
export type PlaybooksPlaybookVersion = z.infer<typeof PlaybooksPlaybookVersionSchema>;

/** `GET /v2/playbooks/:id`. */
export const PlaybooksPlaybookDetailResponseSchema = z.object({
  playbook: PlaybooksPlaybookSummarySchema,
  head: PlaybooksPlaybookVersionSchema.nullable(),
  history: z.array(PlaybooksVersionHistoryEntrySchema),
});
export type PlaybooksPlaybookDetailResponse = z.infer<
  typeof PlaybooksPlaybookDetailResponseSchema
>;

/** `GET /v2/playbooks/:id/versions/:versionId`. */
export const PlaybooksPlaybookVersionResponseSchema = z.object({
  version: PlaybooksPlaybookVersionSchema,
});
export type PlaybooksPlaybookVersionResponse = z.infer<
  typeof PlaybooksPlaybookVersionResponseSchema
>;

/**
 * A play as the `/v2` reads report it.
 *
 * `playbookVersionId` is the play's own ladder row: set at Create Play (the
 * snapshot, plans/2026-09-25-002-playbooks-play-isolation-lean-plan.md Key
 * Technical Decisions 1, 4, 5), whose rungs name identities the play owns.
 * `null` only on a legacy play that has not been snapshotted yet; that play
 * still follows its playbook's head.
 */
export const PlaybooksPlaySummarySchema = z.object({
  id: z.string().min(1),
  campaignSfdcId: z.string().min(1),
  playbookId: z.string().min(1),
  status: z.string().min(1),
  /**
   * Playbooks QA round 4, U10 (P2) / Decision 7 — an OPTIONAL rep-given name.
   * `null` is the common case and means "call it by its playbook's name"
   * (`plays-logic.ts#playDisplayName` owns that fallback, in one place, so
   * the list and the flow heading can never disagree).
   *
   * Nullable, not optional: it is a real column, so a summary that omits it
   * is a server bug rather than a default to fill in.
   */
  name: z.string().nullable(),
  /**
   * U10 (P1) — the soft-archive stamp, as an ISO string. Every list endpoint
   * already filters `archived_at: null`, so in practice a summary reaching a
   * client carries `null` here; it is on the wire because the archive
   * response reports the stamp it just wrote, and a caller holding a summary
   * should be able to tell archived from live without a second read.
   */
  archivedAt: z.string().nullable(),
  /**
   * U10 (P1) — how many `playbooks_play_send` rows this play has. Drives the
   * delete dialog's strength ONLY (`plays-logic.ts#playDeleteWarning`): a
   * play with sends still deletes (Spencer: warn, never block), it just says
   * so first. Deliberately NOT the definition of "unsent" for the usage gate
   * — that is `usage.ts#isUnsentPlay`'s `status !== 'launched'` (Decision 6).
   */
  sendCount: z.number().int().nonnegative(),
  /**
   * `playbooks_play.playbook_version_id` — the play's pinned ladder row
   * (plan 2026-09-25-002, Decision 4: "snapshotted" = non-null). Nullable,
   * not optional: a real column, so a summary without it is a server bug.
   */
  playbookVersionId: z.string().min(1).nullable(),
  /**
   * `playbooks_play.machine_created_at` as an ISO string: set when the play
   * was created through the machine API (Decision 10). `ReviewStep` hides
   * "Apply to campaigns" on such a play. Nullable, not optional.
   */
  machineCreatedAt: z.string().min(1).nullable(),
  createdByEmail: z.string().nullable(),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
});
export type PlaybooksPlaySummary = z.infer<typeof PlaybooksPlaySummarySchema>;

/**
 * `GET /v2/playbooks/:id/usage` — plays grouped by the version they actually
 * resolve to (the "View usage" tree's data shape). A branch group's
 * `versionNumber` is null.
 */
export const PlaybooksPlaybookUsageResponseSchema = z.object({
  playbookId: z.string().min(1),
  groups: z.array(
    z.object({
      versionId: z.string().min(1),
      versionNumber: z.number().int().nullable(),
      isBranch: z.boolean(),
      plays: z.array(PlaybooksPlaySummarySchema),
    }),
  ),
});
export type PlaybooksPlaybookUsageResponse = z.infer<
  typeof PlaybooksPlaybookUsageResponseSchema
>;

/**
 * `GET /v2/sequences/:id/usage` and `GET /v2/segmentations/:id/usage` —
 * "where is this used" (round-3-QA-fixes plan, decisions 6/7). One row per
 * rung, across every non-archived playbook's HEAD ladder, that maps this
 * sequence (or belongs to this segmentation) — the exact same exclusion set
 * as `activeMappingCount`: no branch playbook versions, no archived
 * playbooks, no segmentation `default_mapping`s. Plan 2026-09-27-006 U4 adds
 * "play-only places": a rung a play's OWN pinned ladder still points at this
 * piece after the template head remapped it away (`usage.ts#usageAxes`).
 * `places.length` equals the sequence detail's `activeMappingCount` (both are
 * `usageAxes(...).places.length`, plan 2026-09-27-007 U2), and the save gates
 * recheck against that same number.
 */
/**
 * Round-3b QA fixes, G1 — `campaignName` is the play's campaign's real name
 * (`launcher_sfdc_campaigns.name`), resolved server-side (`usage.ts#usagePlaces`)
 * so the UI can label a play link `Open {campaignName}` instead of the old
 * ambiguous "Open play"/"Open {status} play" (two draft plays on one
 * playbook rendered as identical, indistinguishable links). `null` only when
 * the campaign row itself is missing from the mirror (never filtered on any
 * archived/deleted state — a play still names whatever campaign it was
 * created against, even if that campaign later vanishes from the mirror).
 */
export const PlaybooksUsagePlaySchema = z.object({
  id: z.string().min(1),
  campaignSfdcId: z.string().min(1),
  campaignName: z.string().nullable(),
  status: z.string().min(1),
  /** The rep-given play name (`playbooks_play.name`), `null` = "call it by its playbook" — same contract as `PlaybooksPlaySummarySchema.name`; the sidebar's card titles the play with `playDisplayName`. */
  name: z.string().nullable(),
});
export type PlaybooksUsagePlay = z.infer<typeof PlaybooksUsagePlaySchema>;

/**
 * `segmentationName`/`sequenceName` are nullable only if the identity row is
 * gone (hard delete is Terra-admin-only, so this is a rare edge, not the
 * normal archived case — name lookups here are deliberately NOT filtered on
 * `archived_at`). `sequenceId`/`sequenceName` are both null when the rung is
 * a suppression rung (`mapping: null`).
 *
 * `flowId`/`flowName` (round-3b QA fixes, G2) — the Gong flow bound to this
 * place's Sequence, if any. `flowId` is `sequence.flow_id` (null for a
 * non-Gong sequence, or a Gong sequence with no flow bound yet, or a
 * suppression rung with no sequence at all); `flowName` is the flow's real
 * name, resolved via `_shared/flow/gong-catalog#getGongFlowNames` (a
 * core-owned lookup — see that function's own doc for why the
 * `prisma.gongFlow` call site lives in core, not here), `null` when
 * `flowId` is null OR the id isn't in the (mirrored) Gong catalog — the UI
 * falls back to the raw id in that second case.
 *
 * `playCount` — historically the uncapped total behind a 5-play `plays`
 * slice (Codex F6). Since 2026-09-17 (per-play usage cards, U6) `plays` is
 * NOT capped — every attached play is listed, because each one is a card —
 * so `playCount === plays.length`; kept on the wire so the shape is
 * unchanged. Plan 2026-09-27-006 U4: a play is attached to this place iff
 * its OWN pinned ladder references the piece at this rung id — a play that
 * remapped the rung to its own copy is not listed.
 */
export const PlaybooksUsagePlaceSchema = z.object({
  playbookId: z.string().min(1),
  playbookName: z.string().min(1),
  rungId: z.string().min(1),
  segmentationId: z.string().min(1),
  segmentationName: z.string().min(1).nullable(),
  sequenceId: z.string().min(1).nullable(),
  sequenceName: z.string().min(1).nullable(),
  flowId: z.string().min(1).nullable(),
  flowName: z.string().min(1).nullable(),
  plays: z.array(PlaybooksUsagePlaySchema),
  playCount: z.number().int().nonnegative(),
  /**
   * Round-4 QA fixes, U5 (S1) / Decision 6 — distinct plays of this place's
   * PLAYBOOK (not just this rung) with `usage.ts#isUnsentPlay(play)` true
   * whose OWN pinned ladder references this piece at any rung (plan
   * 2026-09-27-006 U4, `usageAxes#unsentPlayCountByPlaybookId`). Two places
   * sharing one playbook carry the IDENTICAL value here —
   * `usage-gate.ts#distinctUnsentPlays` is what sums correctly across places
   * without double-counting.
   */
  unsentPlayCount: z.number().int().nonnegative(),
});
export type PlaybooksUsagePlace = z.infer<typeof PlaybooksUsagePlaceSchema>;

/**
 * Plan 2026-09-28-007 U9 (rulebook R9, edge (g)) — one playbook whose CURRENT
 * template references the piece (`usage.ts#usageAxes`'s template-ladder
 * loop). This is the confirm ③ names — NOT every playbook that appears in
 * `places` (a place can be a play-only place: the play's own ladder still
 * references the piece at a rung the template itself has since remapped
 * away, per `usageAxes`'s own doc). `name` rides along so the client never
 * needs a second lookup to build "Change the '<name>' playbook too?".
 */
export const PlaybooksTemplatePlaybookSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
});
export type PlaybooksTemplatePlaybook = z.infer<typeof PlaybooksTemplatePlaybookSchema>;

export const PlaybooksUsageResponseSchema = z.object({
  places: z.array(PlaybooksUsagePlaceSchema),
  /**
   * Plan 2026-09-28-006 U2 (Decision 6; rulebook R8, edge (d)) — the
   * non-archived plays (ANY status; the client drops sent ones) whose own
   * ladder points at one of their own copies of this piece (root Library
   * source = this piece, `usage.ts#ownVersionPlaysFromLadders`). "Update N
   * plays" never touches them — the Save menu says "N plays already have their
   * own version and won't change". A play may also appear in `places` (one
   * group still on the Library piece, another on its copy). Defaults to `[]`
   * so a response from a server that predates the field still parses.
   */
  ownVersionPlays: z.array(PlaybooksUsagePlaySchema).default([]),
  /**
   * Plan 2026-09-28-007 U9 (rulebook R9, edge (g)) — every playbook whose
   * CURRENT head ladder references this piece today, replacing the old
   * bare count-only field (a bare count could not tell ③ which
   * playbook(s) to name, and a place's playbook is not necessarily one of
   * these — see {@link PlaybooksTemplatePlaybookSchema}'s own doc). `[]` is
   * edge (g): no playbook uses the piece today, so ③ names the Library
   * instead. Defaults to `[]` so a response from a server that predates the
   * field still parses.
   */
  templatePlaybooks: z.array(PlaybooksTemplatePlaybookSchema).default([]),
});
export type PlaybooksUsageResponse = z.infer<typeof PlaybooksUsageResponseSchema>;

/** `GET /v2/plays?campaignSfdcId=…`. */
export const PlaybooksPlaysResponseSchema = z.object({
  plays: z.array(PlaybooksPlaySummarySchema),
});
export type PlaybooksPlaysResponse = z.infer<typeof PlaybooksPlaysResponseSchema>;

/**
 * `GET /v2/plays/library` (2026-09-17 QA follow-ups, U1) — every non-archived
 * play in the workspace, grouped by campaign, for the Library page's "Plays"
 * section. Deliberately a SEPARATE read from `GET /v2/plays`, which is
 * campaign-scoped by contract (400 without `campaignSfdcId`) and whose
 * summary carries no campaign name — the Library needs the name on every
 * group header, resolved from the `launcher_sfdc_campaigns` mirror the same
 * way `usage.ts` resolves it for the usage sidebar. `campaignName` is `null`
 * only for a campaign row the mirror no longer has (deleted / never synced).
 * Groups are ordered by campaign name (nameless groups last, then by id);
 * plays within a group most-recent-first.
 */
export const PlaybooksPlaysLibraryGroupSchema = z.object({
  campaignSfdcId: z.string().min(1),
  campaignName: z.string().nullable(),
  plays: z.array(PlaybooksPlaySummarySchema).min(1),
});
export type PlaybooksPlaysLibraryGroup = z.infer<typeof PlaybooksPlaysLibraryGroupSchema>;

export const PlaybooksPlaysLibraryResponseSchema = z.object({
  campaigns: z.array(PlaybooksPlaysLibraryGroupSchema),
  /**
   * Codex fixes 2026-09-17, F7 — `playbookId -> name` for EVERY playbook a
   * returned play references, archived playbooks INCLUDED. `GET /v2/library`
   * filters `archived_at: null`, archiving a playbook does not cascade to
   * its plays, and `playDisplayName`'s fallback is the raw id — so a play
   * of an archived playbook rendered a UUID and dropped out of name search.
   * The client merges this UNDER the library's own map (same names for live
   * playbooks; this only adds the ones the library no longer lists).
   */
  playbookNames: z.record(z.string(), z.string()),
});
export type PlaybooksPlaysLibraryResponse = z.infer<typeof PlaybooksPlaysLibraryResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// `/v2` identity-CRUD wire schemas (PR B, U5)
//
// PR C CONTRACT (plan Key Technical Decision 7): the only thing PR C's
// "Save Playbook" / "Save & apply to campaigns" depends on from this unit is
// that an identity exists and its `id` is STABLE. PR C calls
// `createVersion('playbook', …)` directly, never these endpoints, so the
// create response is deliberately minimal — `{ id, versionId, versionNumber }`,
// mirroring `CreateVersionResult`. Adding fields later is additive; renaming
// or dropping `id` is not.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Play-owned creation (plan 2026-09-25-002, Key Technical Decision 13): the
 * play whose copy this new identity is. Optional; omitted = a Library
 * identity, byte-identical to before. The handler validates the play (404
 * unknown, 409 `play_launched` / `identity_archived`) before writing. Shared by
 * the create body (and so `POST /sequences`) and both duplicate bodies.
 */
const OwningPlayIdSchema = z.string().min(1).optional();

/**
 * Body of `POST /v2/segmentations` and `POST /v2/playbooks`. `owningPlayId`
 * applies to `POST /segmentations` (and `POST /sequences`, which extends this
 * schema) only: `POST /playbooks` answers 400 when it is present, because a
 * playbook identity is never play-owned.
 */
export const PlaybooksCreateIdentityRequestSchema = z.object({
  name: z.string().trim().min(1),
  description: z.string().nullable().optional(),
  owningPlayId: OwningPlayIdSchema,
});
export type PlaybooksCreateIdentityRequest = z.infer<typeof PlaybooksCreateIdentityRequestSchema>;

/**
 * Body of `PATCH /v2/segmentations/:id` and `PATCH /v2/playbooks/:id`. At least
 * one field must be present — an empty patch is a caller bug, not a no-op.
 */
export const PlaybooksRenameIdentityRequestSchema = z
  .object({
    name: z.string().trim().min(1).optional(),
    description: z.string().nullable().optional(),
    /**
     * The group this OWNED in-play SEQUENCE rename is for (plan 2026-09-28-008
     * U3, rulebook R5/R8/R14) — see `PlaybooksSetSequenceFlowRequestSchema`'s
     * own doc for the contract. Segmentation/playbook renames never carry it
     * (no client ever sends it there); `renameIdentity` only checks it for
     * `kind === 'sequence'`.
     */
    rungId: z.string().min(1).optional(),
    confirmedRungIds: z.array(z.string().min(1)).min(1).optional(),
  })
  .refine((patch) => patch.name !== undefined || patch.description !== undefined, {
    message: 'Provide at least one of name, description.',
  });
export type PlaybooksRenameIdentityRequest = z.infer<typeof PlaybooksRenameIdentityRequestSchema>;

/** Response of both `POST /v2/{segmentations,playbooks}` and their `/:id/duplicate` routes. */
export const PlaybooksCreateIdentityResponseSchema = z.object({
  id: z.string().min(1),
  versionId: z.string().min(1),
  versionNumber: z.number().int().nullable(),
});
export type PlaybooksCreateIdentityResponse = z.infer<
  typeof PlaybooksCreateIdentityResponseSchema
>;

/**
 * Response of `PATCH /v2/{segmentations,playbooks}/:id` — the updated identity
 * fields, which is exactly the optimistic-echo shape the Config page's rename
 * drawer already merges into its cached library row.
 */
export const PlaybooksRenameIdentityResponseSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable(),
});
export type PlaybooksRenameIdentityResponse = z.infer<
  typeof PlaybooksRenameIdentityResponseSchema
>;

/** Response of `DELETE /v2/{segmentations,playbooks}/:id` — soft delete, so the stamp is reported. */
export const PlaybooksArchiveIdentityResponseSchema = z.object({
  id: z.string().min(1),
  archivedAt: z.string().min(1),
});
export type PlaybooksArchiveIdentityResponse = z.infer<
  typeof PlaybooksArchiveIdentityResponseSchema
>;

// ─────────────────────────────────────────────────────────────────────────────
// Waterfall rung reorder (PR B, U4 — R6)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Body of `POST /v2/playbooks/:id/reorder` — the stable `id`s of the ladder's
 * non-catch-all rungs, in their new priority order. Ids, not positions or array
 * indices: this endpoint's whole job is to rewrite `position`, so a position
 * cannot address the rung being moved across the very write that renumbers it
 * (and `segmentationId` can legitimately repeat across rungs). `id` is the one
 * field a reorder never changes.
 *
 * The catch-all is deliberately absent — it is pinned last and never enters the
 * drag list, so including its id is rejected as a non-permutation.
 * Addressed by PLAYBOOK, never by campaign: a campaign can have several plays
 * pointing at different playbooks (Decision 14).
 *
 * `expectedVersionId` (Codex adversarial-review fix, PR B follow-up) is the
 * playbook version id the client's ladder was actually read from — the head's
 * `versionId` as served by `GET /v2/playbooks/:id`. WITHOUT this, the endpoint
 * re-reads head OUTSIDE any transaction to compute the remapped `rungs`, then
 * writes them in a SEPARATE `createVersion` call that re-reads head again and
 * assigns a fresh `head_number + 1` — a concurrent writer landing in the gap
 * between those two reads is never detected (fresh version number ⇒ no unique-
 * constraint conflict), so the write silently succeeds with content derived
 * from a head the caller never actually saw (a lost update). Required, not
 * optional: the whole point is that every caller proves which head it read.
 */
export const PlaybooksReorderRungsRequestSchema = z.object({
  orderedRungIds: z.array(z.string().min(1)).min(1),
  expectedVersionId: z.string().min(1),
});
export type PlaybooksReorderRungsRequest = z.infer<typeof PlaybooksReorderRungsRequestSchema>;

/**
 * Response of `POST /v2/playbooks/:id/reorder` — the new MAIN-LINE version's
 * identifiers plus the ladder exactly as persisted. `rungs` is echoed (rather
 * than leaving the client to re-derive it) because the server renumbers every
 * `position` to ascend with the new array order; a client that assumed its own
 * numbering would drift from the row it just wrote.
 */
export const PlaybooksReorderRungsResponseSchema = z.object({
  id: z.string().min(1),
  versionId: z.string().min(1),
  versionNumber: z.number().int().nullable(),
  rungs: PlaybookRungsSchema,
});
export type PlaybooksReorderRungsResponse = z.infer<typeof PlaybooksReorderRungsResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Playbook rung save + Play creation (PR C, U3A — R2)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One rung as `POST /v2/playbooks/:id/save` ACCEPTS it — derived from
 * `PlaybookRungSchema` (never redeclared) with exactly two differences, both of
 * which exist because the server owns rung identity and rung order:
 *
 *  - **`position` is omitted entirely.** The array order IS the ladder order and
 *    the server renumbers `0..n-1` from it. Accepting a client `position` would
 *    create a second, contradictable source of truth for the same fact.
 *  - **`id` is optional.** A rung the user just added in the draft has no
 *    identity yet; the endpoint mints `crypto.randomUUID()` for it (Decision 1)
 *    and NEVER re-mints one for a rung that arrived with an id, because every
 *    client-side reference (approvals map, staged edits, per-rung member
 *    queries) is keyed off that id.
 *
 * The draft store's client-only `draftKey` is deliberately ABSENT: it is a
 * browser-session handle for a rung that has no server id yet, so it is stripped
 * here (zod's default object behavior) rather than accepted and stored. Never
 * add it — a `draftKey` reaching `playbooks_playbook_version.rungs` would be a
 * second identity key for the thing `id` already identifies.
 */
export const PlaybooksSaveRungSchema = PlaybookRungSchema.omit({ position: true }).extend({
  id: z.string().min(1).optional(),
});
export type PlaybooksSaveRung = z.infer<typeof PlaybooksSaveRungSchema>;

/**
 * Body of `POST /v2/playbooks/:id/save`.
 *
 * `expectedVersionId` carries the identical contract (and the identical
 * lost-update reasoning) as `PlaybooksReorderRungsRequestSchema`'s: the
 * handler reads head once outside a transaction to check it, then writes in a
 * separate `createVersion` call, so the caller must prove which head its ladder
 * came from. Required, not optional.
 *
 * `applyToCampaignSfdcIds` is the "Save & apply to campaigns" leg on the SAME
 * endpoint rather than a sibling route: applying is defined as "save this
 * ladder, then create a Play per campaign against it", so the two legs must
 * agree on which version the Plays were created against — a sibling route would
 * have to re-read head and could apply against a version the save did not
 * produce. Absent or empty = a plain "Save Playbook".
 *
 * An empty `rungs` array is legal, matching `PlaybookRungsSchema` (the ladder
 * can be emptied mid-build; "must end in a catch-all" is a build-time gate, not
 * a storage invariant).
 */
export const PlaybooksSavePlaybookRungsRequestSchema = z.object({
  rungs: z.array(PlaybooksSaveRungSchema),
  expectedVersionId: z.string().min(1),
  applyToCampaignSfdcIds: z.array(z.string().min(1)).optional(),
});
export type PlaybooksSavePlaybookRungsRequest = z.infer<
  typeof PlaybooksSavePlaybookRungsRequestSchema
>;

/**
 * Response of `POST /v2/playbooks/:id/save` — the new main-line version's
 * identifiers plus the ladder EXACTLY as persisted, so the client learns both
 * the server-minted ids and the derived positions it deliberately did not send.
 * `createdPlayIds` is empty on a plain save.
 */
export const PlaybooksSavePlaybookRungsResponseSchema = z.object({
  id: z.string().min(1),
  versionId: z.string().min(1),
  versionNumber: z.number().int().nullable(),
  rungs: PlaybookRungsSchema,
  createdPlayIds: z.array(z.string().min(1)),
});
export type PlaybooksSavePlaybookRungsResponse = z.infer<
  typeof PlaybooksSavePlaybookRungsResponseSchema
>;

/**
 * Body of `POST /plays/:id/ladder` (plan 2026-09-25-002, U4, Key Technical
 * Decisions 5, 6): the play's whole ladder in the same save-rung shape as
 * `POST /playbooks/:id/save` (ids minted server-side for new rungs, positions
 * from array order). `expectedVersionId` is the ladder the caller read — the
 * play's pin as `GET /playbooks/:id?playId=` served it (that read pins a
 * deploy-window play first, plan 2026-09-27-007 U2) — and is required: a
 * stale value answers 409 `version_conflict`. No
 * `applyToCampaignSfdcIds`: a play's ladder write never creates plays (zod
 * strips the key).
 */
export const PlaybooksSavePlayLadderRequestSchema = z.object({
  rungs: z.array(PlaybooksSaveRungSchema),
  expectedVersionId: z.string().min(1),
});
export type PlaybooksSavePlayLadderRequest = z.infer<typeof PlaybooksSavePlayLadderRequestSchema>;

/**
 * Response of `POST /plays/:id/ladder`: the new ladder row (now the play's
 * pin) and the rungs EXACTLY as written. The write NEVER forks (plan
 * 2026-09-27-006 Key Technical Decision 2) and never substitutes ids (plan
 * 2026-09-28-006 U1, rulebook R11): every posted id is written as-is, and
 * another play's copy is refused `invalid_reference` — so the rungs name
 * Library identities and this play's own copies. The client adopts these
 * rungs.
 */
export const PlaybooksSavePlayLadderResponseSchema = z.object({
  playId: z.string().min(1),
  versionId: z.string().min(1),
  rungs: PlaybookRungsSchema,
});
export type PlaybooksSavePlayLadderResponse = z.infer<typeof PlaybooksSavePlayLadderResponseSchema>;

/**
 * Body of `POST /plays/:id/suppressions` (machine surface; plan
 * 2026-10-30-001 U5, Key Technical Decisions 9–13; rulebook [R17] — a machine
 * write is play-local and never asks): company domains and/or person emails
 * the chat skill was told to exclude from this play.
 *
 * SHAPE ONLY, deliberately: value validation (trim/lowercase/strip `@`,
 * domain and email patterns, at least one value, the per-call caps) lives in
 * `play-suppression-logic.ts#normalizeSuppressionInput`, so the handler's 400s
 * are plain rep sentences at its own `problem()` call sites rather than zod
 * issue text. Either list may be omitted.
 */
export const PlaybooksAddPlaySuppressionsRequestSchema = z.object({
  domains: z.array(z.string()).default([]),
  emails: z.array(z.string()).default([]),
});
export type PlaybooksAddPlaySuppressionsRequest = z.infer<
  typeof PlaybooksAddPlaySuppressionsRequestSchema
>;

/**
 * Response of `POST /plays/:id/suppressions` (Decisions 11, 12):
 *   - `rungId` / `segmentationId` / `segmentationVersionId` name the play's one
 *     machine suppression bucket and the version now holding its values;
 *     `ladderVersionId` is the play's pinned ladder after the call (unchanged
 *     on a merge — a merge never touches the ladder).
 *   - `created` — the bucket was created by this call (the handler answers 201
 *     then, else 200; precedent `PlaybooksCreatePlayResponseSchema`).
 *   - `changed` — anything was written; `false` means every value was already
 *     in the bucket.
 *   - `domains` / `emails` — the FULL normalized set now in the bucket, not
 *     just this call's.
 *   - `playUrl` — the play's direct link (`play-url.ts#playUrl`).
 */
export const PlaybooksAddPlaySuppressionsResponseSchema = z.object({
  playId: z.string().min(1),
  rungId: z.string().min(1),
  segmentationId: z.string().min(1),
  segmentationVersionId: z.string().min(1),
  ladderVersionId: z.string().min(1),
  created: z.boolean(),
  changed: z.boolean(),
  domains: z.array(z.string()),
  emails: z.array(z.string()),
  playUrl: z.string().url(),
});
export type PlaybooksAddPlaySuppressionsResponse = z.infer<
  typeof PlaybooksAddPlaySuppressionsResponseSchema
>;

/**
 * A play's ladder as a play-scoped write rewrote it (plan 2026-09-27-006, Key
 * Technical Decision 10): the new ladder row (now the play's pin) and its
 * rungs. The client adopts these in the same round trip
 * (`adoptSavedPlaybookDraft` + `applyLadderSaveToCache`) so the editor stops
 * addressing the Library id a fork just replaced.
 */
export const PlaybooksPlayLadderRewriteSchema = z.object({
  versionId: z.string().min(1),
  rungs: PlaybookRungsSchema,
});
export type PlaybooksPlayLadderRewrite = z.infer<typeof PlaybooksPlayLadderRewriteSchema>;

/**
 * Body of `POST /plays/:id/fork` (plan 2026-09-27-006 U3.3, Key Technical
 * Decisions 1, 11; rescoped by plan 2026-09-28-006 U1, rulebook R3): give ONE
 * group (`rungId`, required) its own NEW copy of the identity it points at —
 * a Library identity, or one of this play's own copies (copy-of-copy, "Save
 * as new" on a copy two groups share). Only that rung is repointed.
 * `expectedSourceVersionId` is the source head the caller composed against (a
 * Save-as-new sidebar's recorded head); a moved head is 409
 * `version_conflict`. Omitted = no check. Session router only.
 */
export const PlaybooksForkIntoPlayRequestSchema = z.object({
  kind: z.enum(['segmentation', 'sequence']),
  identityId: z.string().min(1),
  rungId: z.string().min(1),
  expectedSourceVersionId: z.string().min(1).optional(),
});
export type PlaybooksForkIntoPlayRequest = z.infer<typeof PlaybooksForkIntoPlayRequestSchema>;

/**
 * Response of `POST /plays/:id/fork`. `identityId` is the play's NEW copy
 * (every fork makes one — there is no idempotent "already had a copy"
 * answer), `forkedFrom` the id the caller addressed, `headVersionId` the
 * copy's v1 (the next write's `expectedVersionId`). `ladder` is the rewritten
 * ladder (only the named rung moved); the server always sends it — the key
 * stays nullable only for wire stability.
 */
export const PlaybooksForkIntoPlayResponseSchema = z.object({
  playId: z.string().min(1),
  kind: z.enum(['segmentation', 'sequence']),
  identityId: z.string().min(1),
  forkedFrom: z.string().min(1),
  headVersionId: z.string().min(1),
  ladder: PlaybooksPlayLadderRewriteSchema.nullable(),
});
export type PlaybooksForkIntoPlayResponse = z.infer<typeof PlaybooksForkIntoPlayResponseSchema>;

/** A content change on the fan-out must carry at least one step (same rule as the copy save). */
const nonEmptyFanOutSteps = SequenceStepsSchema.refine(
  (steps) => steps.length > 0,
  'An update must include at least one step.',
);

/**
 * Body of `POST /plays/:id/update-plays` (plan 2026-09-28-004 §6.2, carried
 * by plan 2026-09-28-006 U1; the endpoint itself is U2; rulebook R8) — option
 * ② of the in-play Save menu: apply ONE change to every unsent play still
 * sharing a LIBRARY piece, each affected group getting its own copy; the
 * Library is never written. Session router only — never the machine surface
 * (R17).
 *
 * `identityId` is the Library identity the origin group uses. `confirmed`
 * NAMES the affected set the rep saw (plan 2026-09-28-007 U2, rulebook R8):
 * `originRungId` is the group the rep is editing in, `plays` every unsent
 * play listed with the groups (rung ids) of it that point at the piece —
 * built by `usage-sidebar-logic.ts#confirmedFanOutSet`, the same function the
 * button label counts from. The server recomputes the set from the locked
 * pins and refuses any difference (origin group gone → 409
 * `version_conflict`; any other difference → 409 `shared_mapping_conflict`).
 * The object is `.strict()` so the retired count-only `confirmedPlayCount`
 * is refused, never silently dropped. `expectedSourceVersionId` is
 * the Library head the rep composed against — required for the content
 * changes (`conditions`, `steps`, `rebind`), optional for settings. `change`
 * must match `kind`: `conditions` only on a segmentation; `steps`, `sender`,
 * `inflection`, `flow`, `rebind` only on a sequence; `name` on either.
 */
export const PlaybooksUpdatePlaysRequestSchema = z
  .object({
    kind: z.enum(['segmentation', 'sequence']),
    identityId: z.string().min(1),
    expectedSourceVersionId: z.string().min(1).optional(),
    confirmed: z.object({
      originRungId: z.string().min(1),
      plays: z
        .array(z.object({ playId: z.string().min(1), rungIds: z.array(z.string().min(1)).min(1) }))
        .min(1),
    }),
    change: z.discriminatedUnion('field', [
      z.object({ field: z.literal('conditions'), conditions: SegmentationConditionsSchema }),
      z.object({
        field: z.literal('steps'),
        steps: nonEmptyFanOutSteps,
        generationInputs: PlaybooksGenerationInputsSchema.optional(),
      }),
      z.object({ field: z.literal('sender'), sender: z.string().nullable() }),
      z.object({ field: z.literal('inflection'), cc: z.string().nullable(), replyTo: z.string().nullable() }),
      z.object({ field: z.literal('flow'), flowId: z.string().trim().min(1) }),
      z.object({ field: z.literal('rebind'), flowId: z.string().trim().min(1), steps: nonEmptyFanOutSteps }),
      z.object({ field: z.literal('name'), name: z.string().trim().min(1) }),
    ]),
  })
  .strict()
  .superRefine((body, ctx) => {
    const { field } = body.change;
    if ((field === 'conditions' || field === 'steps' || field === 'rebind') && body.expectedSourceVersionId === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['expectedSourceVersionId'],
        message: `expectedSourceVersionId is required for a ${field} change.`,
      });
    }
    const segmentationOnly = field === 'conditions';
    const sequenceOnly = field === 'steps' || field === 'sender' || field === 'inflection' || field === 'flow' || field === 'rebind';
    if ((segmentationOnly && body.kind !== 'segmentation') || (sequenceOnly && body.kind !== 'sequence')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['change', 'field'],
        message: `A ${field} change does not apply to a ${body.kind}.`,
      });
    }
  });
export type PlaybooksUpdatePlaysRequest = z.infer<typeof PlaybooksUpdatePlaysRequestSchema>;

/**
 * Response of `POST /plays/:id/update-plays`: one entry per play updated —
 * its rewritten ladder (the client adopts the origin play's) and, per group
 * moved, the new copy and its v1 — plus the plays skipped because they were
 * sent or archived by the time the locks were taken (R13; never an error).
 * `steps` (plan 2026-09-28-007 U2) is the prepared/normalized step list every
 * copy was written with for a `steps` / `rebind` change (Gong tokens
 * normalized, server-owned descriptors re-attached) — the client caches THIS
 * as the copies' head, not what it posted; `null` for every other change.
 */
export const PlaybooksUpdatePlaysResponseSchema = z.object({
  identityId: z.string(),
  updatedPlays: z.array(
    z.object({
      playId: z.string(),
      ladder: PlaybooksPlayLadderRewriteSchema,
      rungs: z.array(z.object({ rungId: z.string(), copyId: z.string(), headVersionId: z.string() })),
    }),
  ),
  skippedLockedPlayIds: z.array(z.string()),
  steps: SequenceStepsSchema.nullable(),
});
export type PlaybooksUpdatePlaysResponse = z.infer<typeof PlaybooksUpdatePlaysResponseSchema>;

/**
 * Body of `POST /plays/:id/clone` — the Review step's Apply (plan 2026-09-27-006
 * U3.4, Key Technical Decision 7). `expectedVersionId` is REQUIRED (plan
 * 2026-09-27-007 Key Decision 6): the play-scoped ladder the rep reviewed
 * (`GET /playbooks/:id?playId=`'s `head.versionId`); a source pin that moved
 * since is 409 `version_conflict`, so Apply never clones edits the rep has not
 * seen. The Review step is the only caller (session router only). The other
 * keys are optional: the new play defaults to the source play's campaign and
 * name. Same `name` rule as `PlaybooksCreatePlayRequestSchema` (trimmed,
 * non-empty, spelled by omission). Answers `PlaybooksCreatePlayResponseSchema`
 * (201, `created: true`).
 */
export const PlaybooksClonePlayRequestSchema = z.object({
  expectedVersionId: z.string().min(1),
  campaignSfdcId: z.string().min(1).optional(),
  name: z.string().trim().min(1).max(200).optional(),
});
export type PlaybooksClonePlayRequest = z.infer<typeof PlaybooksClonePlayRequestSchema>;

/**
 * Body of `POST /v2/plays` — the new-Play route (Key Decision 1's "new-Play"
 * case). `playbookId` is required and carries no default: which playbook a Play
 * runs is the whole content of the decision, and a campaign can host several
 * plays pointing at different playbooks (Decision 14), so there is nothing to
 * infer it from.
 *
 * The play snapshots the playbook's head at creation (plan 2026-09-25-002,
 * Key Technical Decisions 1 and 5): later template edits never reach it. No
 * version input: the snapshot is always the head as of this request.
 */
export const PlaybooksCreatePlayRequestSchema = z.object({
  campaignSfdcId: z.string().min(1),
  playbookId: z.string().min(1),
  /**
   * Originally the optional rep-given name from the Create Play sidebar's
   * play-name field (Playbooks QA round 4, U10 (P2)).
   *
   * QA round 7 (item 10) — as of this round no Terra UI sends this: the
   * Create Play sidebar's field was removed (a second "Name" field next to
   * the Playbook's own name read as a duplicate; a play is named later via
   * Rename on its row or in its header). Kept here for API callers and so a
   * later UI can name a play at create time again without a wire change.
   *
   * `.trim().min(1)` and OPTIONAL rather than nullable: a caller sends the
   * key only when it actually has something to name the play, so a
   * blank/whitespace value on the wire is a caller bug worth a 400 rather
   * than a silent coercion to `null`. "No name" is spelled by OMISSION here;
   * `PlaybooksRenamePlayRequestSchema` is where an explicit `null` (clear it)
   * is legal, because clearing is a real later intent.
   */
  name: z.string().trim().min(1).max(200).optional(),
});
export type PlaybooksCreatePlayRequest = z.infer<typeof PlaybooksCreatePlayRequestSchema>;

/**
 * Response of `POST /v2/plays` — deliberately NOT `PlaybooksPlaySummarySchema`.
 * This is the minimal "what was written" envelope, a superset of the fields
 * the client's previous draft-id stub returned (`{ id, campaignSfdcId,
 * status }`).
 *
 * Plan 2026-09-25-002, U3 (Key Technical Decisions 19, 20):
 *   - `playbookVersionId` is the snapshot's pinned ladder row. Still typed
 *     nullable for the wire's sake; `createPlay` always returns a pin.
 *   - `playUrl` is the play's direct link (`play-url.ts#playUrl`), the same
 *     format the copy save returns, so a machine caller can share it.
 *   - `created` is `false` only when a machine create returned the existing
 *     draft machine-created play for the same campaign and playbook (the
 *     handler answers 200 then; 201 when `true`).
 */
export const PlaybooksCreatePlayResponseSchema = z.object({
  id: z.string().min(1),
  campaignSfdcId: z.string().min(1),
  playbookId: z.string().min(1),
  playbookVersionId: z.string().min(1).nullable(),
  status: z.string().min(1),
  playUrl: z.string().url(),
  created: z.boolean(),
});
export type PlaybooksCreatePlayResponse = z.infer<typeof PlaybooksCreatePlayResponseSchema>;

/**
 * Body of `PATCH /v2/plays/:id` — Playbooks QA round 4, U10 (P2).
 *
 * NULLABLE (unlike the create request's `name`, which is optional-only): a
 * rename can legitimately mean "clear the name and go back to showing the
 * playbook's", and omission-as-clear would make an accidental `{}` destroy
 * the name. So: a trimmed non-empty string sets it, an explicit `null` clears
 * it, and `undefined`/blank is a 400.
 *
 * `.max(200)` matches the create request's bound — one length rule for one
 * column, not two that can drift.
 */
export const PlaybooksRenamePlayRequestSchema = z.object({
  name: z.string().trim().min(1).max(200).nullable(),
});
export type PlaybooksRenamePlayRequest = z.infer<typeof PlaybooksRenamePlayRequestSchema>;

/**
 * Response of `DELETE /v2/plays/:id` — U10 (P1). Mirrors
 * `PlaybooksArchiveIdentityResponseSchema`'s shape (the stamp, not a 204),
 * but is its own schema because a play is not a `VersionKind` and must not
 * be able to drift into that union by sharing its wire type.
 *
 * `archivedAt` is non-nullable here: this response only exists on the path
 * that just wrote the stamp.
 */
export const PlaybooksArchivePlayResponseSchema = z.object({
  id: z.string().min(1),
  archivedAt: z.string().min(1),
});
export type PlaybooksArchivePlayResponse = z.infer<typeof PlaybooksArchivePlayResponseSchema>;

/**
 * Response of `DELETE /v2/plays/:id/dispatch-log` (U5a, playbooks-clay-wire-gaps
 * plan) — how many `launcher_dispatch_log` rows were deleted, and the distinct
 * `dispatch_id`s they belonged to (a play can have more than one send, so more
 * than one dispatch id; a play with no sends at all deletes zero rows and
 * answers `{ deletedRows: 0, dispatchIds: [] }` rather than a 404 — the play
 * itself still 404s if it doesn't exist, via `wipePlayDispatchLogEndpoint`'s
 * own existence check). Never reports on `playbooks_play_send` — those rows
 * are untouched, an immutable audit trail.
 */
export const PlaybooksWipePlayDispatchLogResponseSchema = z.object({
  deletedRows: z.number().int().nonnegative(),
  dispatchIds: z.array(z.string().min(1)),
});
export type PlaybooksWipePlayDispatchLogResponse = z.infer<
  typeof PlaybooksWipePlayDispatchLogResponseSchema
>;

/**
 * Body of `POST /v2/plays/:id/dispatch-log/retry` (plan 2026-09-26-003, U4).
 *
 * `dispatchId` names ONE send (real `pbsend_…` or test `pbtest_<playId>_…`);
 * the server refuses a dispatch that is not this play's. `logIds` narrows to
 * specific rows (a row's own "Retry" button); omitted means every retryable
 * row of that dispatch — a failed START or a proven-safe Clay refusal, both
 * arms ("Retry all stopped or failed"). Capped at 500 — the most
 * rows one `launcher_dispatch_log` INSERT batch writes — so one click can
 * never fan out into an unbounded Clay-query storm.
 */
export const PlaybooksRetryDispatchRequestSchema = z.object({
  dispatchId: z.string().min(1),
  logIds: z.array(z.string().min(1)).min(1).max(500).optional(),
});
export type PlaybooksRetryDispatchRequest = z.infer<typeof PlaybooksRetryDispatchRequestSchema>;

/**
 * What happened to one row a retry looked at.
 *
 *   requeued         — Clay confirmed no run; the row is `queued` again and a fresh run started.
 *   clay_has_run     — Clay already HAS a run for this member; the row was repaired to `sent`, nothing resent.
 *   not_retryable    — not a start failure (a skip, a row Clay reported on, a row that is not `failed`, or not in this send).
 *   already_retried  — someone else's retry won the race for this row.
 *   no_payload       — Terra no longer has what this member was sent; nothing resent.
 *   clay_unavailable — Clay couldn't be asked (or can't be, for this transport); nothing resent.
 *   start_failed     — Clay confirmed no run, but starting the resend failed; the row is `failed` again.
 *   clay_no_runs     — Clay shows no runs at all for this send, so "no run for this member" proves nothing; nothing resent.
 *   too_recent       — the last START began under a minute ago and may still be settling in Clay; nothing checked or resent.
 *   copy_rebuild_failed — a Clay REFUSAL whose message could not be rebuilt from the play as it is now
 *                      (the member is no longer routed to a mapped group, or the copy still has an
 *                      unfillable merge field); the row is left exactly as it was. Sharing rules (m).
 */
export const PLAYBOOKS_RETRY_DISPATCH_OUTCOMES = [
  'requeued',
  'clay_has_run',
  'not_retryable',
  'already_retried',
  'no_payload',
  'clay_unavailable',
  'start_failed',
  'clay_no_runs',
  'too_recent',
  'copy_rebuild_failed',
] as const;
export const PlaybooksRetryDispatchOutcomeSchema = z.enum(PLAYBOOKS_RETRY_DISPATCH_OUTCOMES);
export type PlaybooksRetryDispatchOutcome = z.infer<typeof PlaybooksRetryDispatchOutcomeSchema>;

/**
 * `launcher_dispatch_log.result_data.auto_retry` — one entry per AUTOMATIC
 * retry of a run Clay broke on a network error (plan 2026-10-14-002 U0e,
 * sharing rules (o)), appended by the triage's claim in `dispatch-retry.ts`.
 * Never cleared by a retry's CAS (not a `clay_*` key), so the row keeps its
 * history across attempts and the cap of 3 is read from its length. `reason`
 * is WHY Terra could retry without asking: a test send, or Gong showing the
 * person not in the flow.
 */
export const DispatchAutoRetryEntrySchema = z.object({
  attempt: z.number().int().positive(),
  at: z.string().min(1),
  reason: z.enum(['test_send', 'gong_not_enrolled']),
});
export const DispatchAutoRetryHistorySchema = z.array(DispatchAutoRetryEntrySchema);
export type DispatchAutoRetryEntry = z.infer<typeof DispatchAutoRetryEntrySchema>;

/**
 * `launcher_dispatch_log.result_data.crash_triage` — what Terra decided about
 * the LATEST network-error crash of the row (`run` = that crashed Clay run's
 * id). Written by `dispatch-crash-triage.ts` only; a rep's manual retry
 * removes it. Read by the classifier for the row's banner.
 */
export const DISPATCH_CRASH_TRIAGE_OUTCOMES = [
  /** Terra is deciding (claimed, waiting out the backoff, asking Gong). */
  'checking',
  /** Terra retried it on its own. */
  'requeued',
  /** Gong shows the person in the flow: marked delivered, not resent. */
  'repaired_enrolled',
  /** Gong couldn't be asked: left for the rep, with the confirm. */
  'gong_unavailable',
  /** Three automatic retries already: left for the rep. */
  'cap_reached',
  /** No way to prove nothing was delivered (Inflection, unknown channel): left for the rep. */
  'manual_only',
  /** No stored message to resend: left for the rep. */
  'no_payload',
] as const;
export const DispatchCrashTriageSchema = z.object({
  run: z.string().min(1),
  at: z.string().min(1),
  outcome: z.enum(DISPATCH_CRASH_TRIAGE_OUTCOMES),
});
export type DispatchCrashTriage = z.infer<typeof DispatchCrashTriageSchema>;

/**
 * One Clay run a retry CAS REPLACED (`result_data.prior_runs[]`, second
 * review-fix pass U6, C5a-1): the crash claim and the refusal claim append the
 * run they clear, with the `retry_count` of the attempt it belonged to. Never
 * cleared — history, like `auto_retry`. The START arm counts these so a run
 * Terra already knows failed is never mistaken for the current attempt's.
 */
export const DispatchPriorRunSchema = z.object({
  routine_run_id: z.string().nullable(),
  workflow_run_id: z.string().nullable(),
  retry_count: z.number().int().nonnegative(),
  at: z.string().min(1),
});
export type DispatchPriorRun = z.infer<typeof DispatchPriorRunSchema>;
export const DispatchPriorRunsSchema = z.array(DispatchPriorRunSchema);

/** Response of `POST /v2/plays/:id/dispatch-log/retry`. `requeued` counts the `requeued` results. */
export const PlaybooksRetryDispatchResponseSchema = z.object({
  requeued: z.number().int().nonnegative(),
  results: z.array(
    z.object({
      logId: z.string().min(1),
      campaignMemberId: z.string().nullable(),
      contactName: z.string().nullable(),
      outcome: PlaybooksRetryDispatchOutcomeSchema,
    }),
  ),
});
export type PlaybooksRetryDispatchResponse = z.infer<typeof PlaybooksRetryDispatchResponseSchema>;

/**
 * Body of `POST /v2/plays/:id/dispatch-log/recheck` (plan 2026-09-27-003, U4):
 * re-ask Clay about one send's rows the status poll gave up on — always the
 * whole send (every eligible row of it; `dispatch-recheck-eligibility.ts`).
 * A stale caller still sending `logIds` has it stripped (`z.object` drops
 * unknown keys) and gets the whole send.
 */
export const PlaybooksRecheckDispatchRequestSchema = z.object({
  dispatchId: z.string().min(1),
});
export type PlaybooksRecheckDispatchRequest = z.infer<typeof PlaybooksRecheckDispatchRequestSchema>;

/**
 * What a re-check did with one row.
 *
 *   updated        — Clay answered; the row now carries its real outcome.
 *   still_running  — Clay says the run is still going; nothing written.
 *   unavailable    — Clay couldn't be asked, rate-limited us, or can't say; nothing written.
 *
 * A row that isn't eligible for a re-check is simply left out of `results`.
 */
export const PLAYBOOKS_RECHECK_DISPATCH_OUTCOMES = ['updated', 'still_running', 'unavailable'] as const;
export const PlaybooksRecheckDispatchOutcomeSchema = z.enum(PLAYBOOKS_RECHECK_DISPATCH_OUTCOMES);
export type PlaybooksRecheckDispatchOutcome = z.infer<typeof PlaybooksRecheckDispatchOutcomeSchema>;

/** Response of `POST /v2/plays/:id/dispatch-log/recheck`. `updated` counts the `updated` results. */
export const PlaybooksRecheckDispatchResponseSchema = z.object({
  updated: z.number().int().nonnegative(),
  results: z.array(
    z.object({
      logId: z.string().min(1),
      outcome: PlaybooksRecheckDispatchOutcomeSchema,
    }),
  ),
});
export type PlaybooksRecheckDispatchResponse = z.infer<typeof PlaybooksRecheckDispatchResponseSchema>;

/**
 * The zod mirror of `_shared`'s `ValueDefect` (plan 2026-10-14-002 U0f): a
 * member VALUE that is itself merge-token shaped (a contact whose Salesforce
 * name is literally `{{unknown}}`). A data gap in the record — the token's
 * declared fallback fired and the member still sends — reported as a warning,
 * never a defect. `value` is the placeholder text, not a contact's real name.
 */
export const PlaybooksValueDefectSchema = z.object({
  token: z.string().min(1),
  value: z.string(),
});
export type PlaybooksValueDefect = z.infer<typeof PlaybooksValueDefectSchema>;
export type PlaybooksValueDefectMirrorsShared = AssertTrue<
  Equal<z.infer<typeof PlaybooksValueDefectSchema>, ValueDefect>
>;

/**
 * Where "What was sent" got its copy (plan 2026-09-27-003, U5), best first:
 *
 *   clay_final       — Clay's own final copy (`result_data.clay_final_copy`, V3 final-status v2).
 *   terra_payload    — the job Terra stored on the row (`launcher_dispatch_log.payload`).
 *   terra_dbos_input — the original DBOS input of the dispatch run.
 *   reconstructed    — rebuilt from the play's lock / heads (`previewMemberPayload`).
 */
export const PLAYBOOKS_SENT_COPY_SOURCES = ['clay_final', 'terra_payload', 'terra_dbos_input', 'reconstructed'] as const;
export const PlaybooksSentCopySourceSchema = z.enum(PLAYBOOKS_SENT_COPY_SOURCES);
export type PlaybooksSentCopySource = z.infer<typeof PlaybooksSentCopySourceSchema>;

/**
 * One step as the recipient reads it. `kind`: `email`, `reply` (an email past
 * the first with no subject of its own — Gong threads it), `gong_own` (a blank
 * non-email slot: Gong runs its own step), `other` (a non-email step the rep
 * wrote in). `bodyHtml` is UNTRUSTED — the client renders it only through
 * `SanitizedHtml`.
 */
export const PlaybooksSentCopyStepSchema = z.object({
  ordinal: z.number().int().positive(),
  kind: z.enum(['email', 'reply', 'gong_own', 'other']),
  subject: z.string().nullable(),
  bodyHtml: z.string(),
  actionType: z.string(),
});
export type PlaybooksSentCopyStep = z.infer<typeof PlaybooksSentCopyStepSchema>;

/** Response of `GET /v2/plays/:id/dispatch-log/:logId/sent-copy`. */
export const PlaybooksSentCopyResponseSchema = z.object({
  source: PlaybooksSentCopySourceSchema,
  /** The rep-facing caption for a source other than `clay_final`. */
  note: z.string(),
  channel: z.string().nullable(),
  /** Clay's resolved sender, else the one Terra asked for — never invented. */
  sender: z.string().nullable(),
  /** The Gong flow's name from the mirror, or a non-Gong channel's own flow label; `null` when unknown. */
  flowTag: z.string().nullable(),
  /**
   * Where "Open run in Clay" goes (`clay-run-link.ts`): the run's trace page
   * when the row carries a Clay run id — or the first open of the row finds it
   * (`sent-copy.ts#lookUpWorkflowRunId`, saved once) — else the V3 workflow's page,
   * built from the routine id the dispatch job carried; `null` when Terra no
   * longer has the job.
   */
  clayRunUrl: z.string().nullable(),
  /**
   * What `clayRunUrl` opens, so the label can say so: `run` (that run's trace)
   * or `workflow` (the workflow page, run not known yet). Absent when there is
   * no link, and from a server that predates it — the client then treats a
   * link as the workflow page.
   */
  clayRunUrlKind: z.enum(['run', 'workflow']).optional(),
  steps: z.array(PlaybooksSentCopyStepSchema),
  /**
   * U0f — values in this person's Salesforce record that were themselves
   * placeholders, so a fallback ("there") went out in their copy. Read from
   * the person's CURRENT record when the row is opened; empty when the record
   * has since been fixed, the person left the campaign, or nothing was wrong.
   */
  dataWarnings: z.array(PlaybooksValueDefectSchema).default([]),
});
export type PlaybooksSentCopyResponse = z.infer<typeof PlaybooksSentCopyResponseSchema>;

/**
 * The most people one TEST send may build. A test exists to check copy on a
 * handful of real records, not to mail a roster (Spencer's call, 2026-10-14).
 * A WHOLE-ROSTER test of a play that routes more than this is SAMPLED to this
 * many (`send-payload.ts#pickTestSample`, plan 2026-10-15-005), never refused;
 * only a `testMemberIds` list longer than this is refused
 * (`test_send_too_large`). Lives here — a leaf both the client and `send.ts`
 * import; `send.ts` re-exports it.
 */
export const PLAYBOOKS_TEST_SEND_MAX_MEMBERS = 100;

/**
 * Body of `POST /v2/plays/:id/send` (PR D, U5a; `isTest` added by U4 — R14).
 *
 * `memberIds` is the ROUTED roster the rep reviewed, not a filter: the server
 * re-derives the ladder at the send instant and `routeMembersToRungs` refuses
 * any set disagreement (Key Technical Decision 4), so this list is a
 * fail-closed cross-check rather than the source of who gets a send. That is
 * also why it is `.min(1)`: "send to nobody" is a caller bug worth a 400, not a
 * successful no-op send row.
 *
 * `isTest` IS REQUIRED AND HAS NO DEFAULT. This is deliberate and is the whole
 * point of the flag: the Clay workflow's `Normalize` node defaults an ABSENT
 * `is_test` to TRUE, which is why nothing Terra "sent" ever actually went out
 * (`.baton/2026-09-15-terra-merge-field-scope-packet.md`, item 14). A default
 * here would re-create that bug one layer up — a caller that forgets the flag
 * must get a 400, not a silently-chosen mode. `true` resolves through exactly
 * the same code path as `false` and writes its own `is_test` send row (plan
 * 2026-10-14-002 U0b(g) revised Key Technical Decision 6, which once wrote
 * none) — so a test has a Dispatch-log card like any send.
 *
 * `testMemberIds` (plan 2026-10-15-003) makes a TEST for chosen people only —
 * the Review step's one-person test. It is valid ONLY with `isTest: true` (the
 * refine below; a real send for a subset is not a thing). `memberIds` is still
 * the FULL routed roster and is still cross-checked exactly; the server
 * narrows to the chosen people only after that check passes. The test cap
 * ({@link PLAYBOOKS_TEST_SEND_MAX_MEMBERS}) caps the chosen list; a
 * whole-roster test over it is sampled to it (plan 2026-10-15-005).
 */
export const PlaybooksSendPlayRequestSchema = z
  .object({
    memberIds: z.array(z.string().min(1)).min(1),
    isTest: z.boolean(),
    testMemberIds: z.array(z.string().min(1)).min(1).max(PLAYBOOKS_TEST_SEND_MAX_MEMBERS).optional(),
  })
  .refine((body) => body.testMemberIds === undefined || body.isTest, {
    path: ['testMemberIds'],
    message: 'testMemberIds is only valid on a test send (isTest: true)',
  });
export type PlaybooksSendPlayRequest = z.infer<typeof PlaybooksSendPlayRequestSchema>;

/**
 * The zod mirror of `sequence-token-flags.ts`'s `NonBraceDefectKind` — the
 * narrower five-member subset of `TokenFlagKindSchema` (declared further down
 * this file): a single brace, a double bracket, a bare angle bracket, a dead
 * `href`, or rep-facing planning scaffolding left in the copy. A separate enum
 * (rather than reusing `TokenFlagKindSchema` there) because these five are
 * diagnosed and rendered differently from the four remaining `TokenFlagKind`
 * members (`gong_unmapped` / `conditional` / `unknown` / `company_name`), not
 * because they block a send — **as of the binding product ruling in
 * playbooks-widen-copy-defect-detection, NONE of the five ever skip a
 * member.** They are WARN-ONLY: reported on `PlaybooksMemberResolutionSchema`
 * (`nonBraceDefects`) and on the resolution report's `copyWarnings`, but the
 * member with only a non-brace issue still sends. Gong parses a lone
 * `single_brace` as its OWN merge-field syntax and renders it in red to the
 * recipient exactly like a broken `{{…}}` token (verified live
 * 2026-09-17) — so `single_brace` is the most severe of the five to surface
 * prominently, but it still does not skip the member; fixing that needs a
 * Clay-workflow change nobody has chosen yet. This is unrelated to, and does
 * not change, `PlaybooksSendDefectSchema.unknownTokens`, which is still the
 * one thing that skips a member.
 */
export const NonBraceDefectKindSchema = z.enum([
  'single_brace',
  'double_bracket',
  'angle_bracket',
  'dead_href',
  'scaffolding',
]);
export type NonBraceDefectKindMirrorsClassifier = AssertTrue<
  Equal<z.infer<typeof NonBraceDefectKindSchema>, NonBraceDefectKind>
>;

/**
 * One non-`{{}}` defect on the wire — `sequence-token-flags.ts`'s
 * `NonBraceDefectFlag`, mirrored the same way {@link EmptyFallbackSchema} is.
 */
export const PlaybooksNonBraceDefectSchema = z.object({
  kind: NonBraceDefectKindSchema,
  token: z.string().min(1),
  stepPositions: z.array(z.number().int().nonnegative()),
});
export type PlaybooksNonBraceDefect = z.infer<typeof PlaybooksNonBraceDefectSchema>;

/**
 * One member skipped for a COPY DEFECT (U4, Key Technical Decision 5).
 *
 * A defect is, again, ALWAYS an UNKNOWN-`{{…}}`-TOKEN skip (the binding
 * product ruling in playbooks-widen-copy-defect-detection): a typo or a
 * Gong-spelled token that no Terra resolver can fill (`{{Frist Name}}`,
 * `{{Companny Name}}`). A CLAY-OWNED token is never a defect (2026-09-16): it
 * is a declared handoff, reported on the payload's `deferred_tokens` and the
 * report's `deferredTokens`, and the member sends. A defective member gets no
 * dispatch entry and a `result = 'failed'` `launcher_dispatch_log` row
 * instead; every other member of the same send still goes out (R11:
 * per-member failure, never a play-level halt).
 *
 * An EMPTY value for a KNOWN token is NOT a defect — it is a data gap, its
 * declared `emptyFallback` fires, and the member sends.
 *
 * `stepPositions` is 0-BASED (a step's own `position`); the rep-facing 1-based
 * label lives in the log row's `error` string, via `memberDefectError`.
 *
 * ── TWO REASONS A MEMBER IS SKIPPED (QA round 8, U6 — 2026-09-22) ──────────
 * `reason` names which one. `'unknown_token'` is everything described above,
 * and it is the DEFAULT, so an entry written by an older server (or replayed
 * from an older client) parses unchanged. `'sender_missing'` is the new
 * per-member SENDER skip: the rung's sender is the met-with (scanner) token
 * and this member's record carries no `primary_met_with_email`, so there is
 * nobody to send as. It is deliberately NOT a copy state — no token is
 * unknown and the copy is fine — which is why it gets its own reason instead
 * of being squeezed into `unknownTokens`.
 *
 * `detail` is the rep-facing sentence for a `'sender_missing'` entry, carried
 * ON THE WIRE rather than rebuilt client-side, so the resolution report, the
 * Dispatch Inspector and the `launcher_dispatch_log.error` row can never say
 * three different things about one skip. It is `null` for a copy defect,
 * whose line is composed from `unknownTokens`/`stepPositions` by
 * `reportDefectSummary`.
 *
 * `unknownTokens` is `.min(1)` VIA THE `superRefine` BELOW, keyed on `reason`,
 * rather than at the array level: an `'unknown_token'` entry with no tokens is
 * still a bug, but a `'sender_missing'` entry legitimately has none. The same
 * refinement requires a non-null `detail` on a `'sender_missing'` entry, so
 * neither reason can be constructed without the thing that makes it legible
 * to the rep. Its ELEMENT type stays
 * plain `z.string()`, NOT `.min(1)` (Playbooks send fix plan U4, Codex B1): a
 * `{{ }}`/`{{}}` token is a real, resolver-reported unknown token with the key
 * `''` (`_shared/launcher-copy-tokens.ts`'s `TOKEN_PATTERN_SOURCE` accepts
 * zero chars on purpose) — the wire must carry whatever the resolver reports
 * rather than reject the member's own defect report.
 *
 * `nonBraceDefects` (playbooks-widen-copy-defect-detection) is SUPPLEMENTARY
 * CONTEXT only, never a reason this entry exists: the five non-`{{}}` classes
 * (single brace, double bracket, bare angle bracket, dead `href`, planning
 * scaffolding) are warn-only and never skip a member on their own (see
 * {@link NonBraceDefectKindSchema}'s doc). When a member is ALSO skipped for
 * an unknown `{{…}}` token, its non-brace hits ride along here so the
 * Dispatch Inspector can show both causes on the one `failed` row; a member
 * whose ONLY issue is non-brace never reaches this schema at all — it sends,
 * and its non-brace hits appear on
 * `PlaybooksResolutionReportResponseSchema.copyWarnings` instead. `.default([])`
 * for a client hitting an older server mid-deploy.
 */
export const PlaybooksSendDefectReasonSchema = z.enum(['unknown_token', 'sender_missing']);

export const PlaybooksSendDefectSchema = z
  .object({
    memberId: z.string().min(1),
    contactName: z.string().nullable(),
    reason: PlaybooksSendDefectReasonSchema.default('unknown_token'),
    detail: z.string().nullable().default(null),
    unknownTokens: z.array(z.string()),
    stepPositions: z.array(z.number().int().nonnegative()),
    nonBraceDefects: z.array(PlaybooksNonBraceDefectSchema).default([]),
  })
  .superRefine((defect, ctx) => {
    if (defect.reason === 'unknown_token' && defect.unknownTokens.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['unknownTokens'],
        message:
          'a copy defect must name at least one unknown token — a member with no unknown token is not skipped',
      });
    }
    if (defect.reason === 'sender_missing' && defect.detail === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['detail'],
        message: 'a sender skip must carry the rep-facing sentence explaining it',
      });
    }
  });
export type PlaybooksSendDefect = z.infer<typeof PlaybooksSendDefectSchema>;

/**
 * Response of `POST /v2/plays/:id/send` — the receipt of an ACCEPTED send,
 * real or test (plan 2026-10-14-002 U0b). The request answers in about a
 * second whatever the roster size: it froze the roster, wrote one `queued`
 * Dispatch-log row per person and enqueued the background build, and every
 * member's outcome (built, held back for a copy or sender problem, removed
 * from the campaign, started, delivered) appears on their own row afterwards.
 *
 * `sendId` is the `playbooks_play_send` row — now written for a TEST send too
 * (`is_test = true`, U0b(g)), so it is never null. `dispatchId` is what every
 * log row carries (`pbsend_…` / `pbtest_<playId>_…`). `total` is how many
 * people the send was approved for (one row each). `startsPending` is always
 * `true`: nothing has gone to Clay yet when this answers — the toast must say
 * "started", never "sent".
 *
 * `sampledFrom` (plan 2026-10-15-005) is present ONLY when a whole-roster test
 * picked fewer people than the play routes (a sample of `total`): how many it
 * routes. Absent on every other send — a real send's receipt never has the key.
 *
 * The old `queued` / `startFailed` / `defects` / `logWriteFailed` fields are
 * gone: defects are per-row terminal states now, written by the build, and
 * the log rows are written in the send's own transaction (no post-commit
 * write that could fail separately).
 */
export const PlaybooksSendPlayResponseSchema = z.object({
  sendId: z.string().min(1),
  dispatchId: z.string().min(1),
  isTest: z.boolean(),
  total: z.number().int().min(0),
  startsPending: z.literal(true),
  sampledFrom: z.number().int().positive().optional(),
});
export type PlaybooksSendPlayResponse = z.infer<typeof PlaybooksSendPlayResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Dispatch inspector reads (PR D, U6a) — R9 / Key Technical Decision 11
//
// Two reads behind the Review step's inspector: the send history with each
// send's dispatch-log rows, and one member's RECONSTRUCTED payload. The log
// row keeps its snake_case DB column names on the wire (every other `/v2`
// shape is camelCase) on purpose — `launcher_dispatch_log` is launcher-owned,
// both apps' inspectors read the same columns, and renaming them here would
// invent a second vocabulary for one table.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A `TIMESTAMPTZ` column on its way to the wire: `Date` from a Prisma raw
 * read, an ISO string once it has been through JSON (this schema also runs on
 * the response side, where the value is already a string), `null` when unset.
 *
 * The transform is what makes one schema cover both sides — the OUTPUT is
 * always an ISO string or null, so the parsed row is JSON-ready and a
 * re-parse of the response is idempotent.
 */
const DispatchLogTimestampSchema = z
  .union([z.string().min(1), z.date(), z.null()])
  .transform((value) =>
    value === null || typeof value === 'string' ? value : value.toISOString(),
  );

/**
 * One `launcher_dispatch_log` row as the inspector reads it (columns per
 * `prisma/schema/migrations/20260312_launcher_dispatch_log` + `processing_at`
 * from `20260314_launcher_dispatch_processing_at`).
 *
 * `id` and `dispatch_id` are the only required columns: the row id is the
 * inspector's list key and `dispatch_id` is the ONLY join back to the
 * `playbooks_play_send` row (Key Technical Decision 2), so a row missing
 * either is unusable rather than merely sparse. Everything else is nullable
 * because the workflow fills it in over the row's life — a `queued` row has
 * no `processing_at`, `result_status`, `result_at` or `result_data` yet.
 *
 * `status_column` and `campaign_id` are deliberately absent: Playbooks writes
 * NULL to the former (it routes by segmentation — `bucket` carries the
 * segmentation name) and the latter is already implied by the play.
 *
 * `result_data` is `z.unknown()` — it is whatever Clay posted back, and the UI
 * renders it as JSON rather than reading fields out of it.
 */
export const PlaybooksDispatchLogRowSchema = z.object({
  id: z.string().min(1),
  dispatch_id: z.string().min(1),
  campaign_member_id: z.string().nullable(),
  contact_id: z.string().nullable(),
  contact_name: z.string().nullable(),
  contact_email: z.string().nullable(),
  bucket: z.string().nullable(),
  flow_id: z.string().nullable(),
  channel: z.string().nullable(),
  subject: z.string().nullable(),
  body: z.string().nullable(),
  sender_email: z.string().nullable(),
  is_test: z.boolean().nullable(),
  result: z.string().nullable(),
  error: z.string().nullable(),
  dispatched_at: DispatchLogTimestampSchema,
  processing_at: DispatchLogTimestampSchema,
  result_status: z.string().nullable(),
  result_at: DispatchLogTimestampSchema,
  result_data: z.unknown(),
  /**
   * Retry bookkeeping (plan 2026-09-26-003, U5 —
   * `20260926170000_launcher_dispatch_log_retry`): how many times a rep
   * retried the row, when, and who. OPTIONAL on the wire so a fixture or a
   * response built before the migration still parses; the SELECT always
   * returns them (`retry_count` is `NOT NULL DEFAULT 0`).
   */
  retry_count: z.number().int().nonnegative().optional(),
  last_retry_at: DispatchLogTimestampSchema.optional(),
  last_retry_by_email: z.string().nullable().optional(),
});
export type PlaybooksDispatchLogRow = z.infer<typeof PlaybooksDispatchLogRowSchema>;

/**
 * One send's progress, computed on the SERVER (plan 2026-10-14-002 U0b(d)) so a
 * 3,000-person send can show a running line — "Built 1,200 · with Clay 1,150 ·
 * confirmed 900 of 3,000" — without the client holding every row.
 *
 * `built` counts rows whose message exists (stored job) or that the build
 * held back (terminal) — i.e. `total` minus rows still `queued` with no job.
 * `handedToClay` counts rows a dispatch run has picked up (`processing_at`
 * set). `byStatus` is `dispatch-rep-status.ts#classifyRepStatus` over the
 * rows — the ONE classifier, the same counts the chips show — and `inFlight`
 * is its `in_progress` count (not-built rows included). `finished` is the
 * background build's own stamp (`playbooks_play_send.finished_at`); a send
 * from before the background build existed counts as finished.
 */
/**
 * The send's outcome buckets (plan 2026-10-14-002 U0e (d)) — every person in
 * exactly one, decided by `send-summary.ts#sendOutcomeOf` from the ONE
 * classifier, so `sum === total` is the send's reconciliation (asserted and
 * logged server-side, `send-history.ts#summarizeSend`):
 *
 *   delivered        Clay delivered (real send).
 *   testOk           a test send Clay finished (a test never delivers).
 *   stoppedByClay    Clay decided not to send (a stop, a non-send word).
 *   heldBackByTerra  Terra held the person back at build (copy defect, no sender, …).
 *   failedRetryable  failed to start, or broke in Clay, and Retry can act.
 *   failedFinal      failed or broke and Retry cannot act.
 *   inProgress       still on its first attempt.
 *   retrying         in flight again on a retry (manual or Terra's own).
 *
 * `heldBackData` (a Salesforce data gap) is U0f's and not here yet.
 */
export const SEND_OUTCOME_KEYS = [
  'delivered',
  'testOk',
  'stoppedByClay',
  'heldBackByTerra',
  'failedRetryable',
  'failedFinal',
  'inProgress',
  'retrying',
] as const;
const SendOutcomeCountSchema = z.number().int().nonnegative();
export const PlaybooksSendOutcomesSchema = z.object({
  delivered: SendOutcomeCountSchema,
  testOk: SendOutcomeCountSchema,
  stoppedByClay: SendOutcomeCountSchema,
  heldBackByTerra: SendOutcomeCountSchema,
  failedRetryable: SendOutcomeCountSchema,
  failedFinal: SendOutcomeCountSchema,
  inProgress: SendOutcomeCountSchema,
  retrying: SendOutcomeCountSchema,
});
export type PlaybooksSendOutcomes = z.infer<typeof PlaybooksSendOutcomesSchema>;

export const PlaybooksSendSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  built: z.number().int().nonnegative(),
  handedToClay: z.number().int().nonnegative(),
  inFlight: z.number().int().nonnegative(),
  finished: z.boolean(),
  byStatus: z.record(RepDispatchStatusSchema, z.number().int().nonnegative()),
  /** U0e (d). Required: web and API ship as one image (review-fix U10 — a tab open across the deploy reloads). */
  outcomes: PlaybooksSendOutcomesSchema,
  /**
   * U0a — what the Dispatch log needs to act WITHOUT holding every row (the
   * rows are paged, so the client can no longer derive these):
   *   `live`             poll again — `shouldPoll` over the rows, or the build still running;
   *   `retryable`        how many people "Retry all stopped or failed" would act on;
   *   `retryableCrashes` how many of those resend a stored job Terra cannot prove undelivered
   *                      on a REAL send — network-error crashes AND any other Clay failure
   *                      (PR 1 U2; the wire name is kept, the meaning widened). The retries
   *                      that could deliver twice, so the confirm says so;
   *   `needsRecheck`     rows the automatic Clay re-check would ask about.
   * Each is read off the SAME rules the row buttons use (`dispatch-inspector-logic.ts`).
   */
  live: z.boolean(),
  retryable: z.number().int().nonnegative(),
  retryableCrashes: z.number().int().nonnegative(),
  needsRecheck: z.number().int().nonnegative(),
});
export type PlaybooksSendSummary = z.infer<typeof PlaybooksSendSummarySchema>;

/**
 * Response of `GET /v2/plays/:id/sends` — the send history, newest first, each
 * send carrying the `launcher_dispatch_log` rows that share its `dispatch_id`.
 *
 * `dispatchId` is nullable and `log` is then empty: a `playbooks_play_send` row
 * written before PR D has no dispatch at all, and the inspector must show it as
 * a send that happened rather than hide it.
 *
 * `resolvedRefs` rides along because it IS the lock — the inspector labels each
 * log row's rung from it, and no second read can reconstruct what the send
 * resolved to after a head has moved.
 */
export const PlaybooksPlaySendsResponseSchema = z.object({
  sends: z.array(
    z.object({
      id: z.string().min(1),
      dispatchId: z.string().min(1).nullable(),
      sentAt: z.string().min(1),
      sentByEmail: z.string().nullable(),
      memberCount: z.number().int().nullable(),
      playbookVersionId: z.string().min(1),
      resolvedRefs: ResolvedRefsSchema,
      /**
       * The send's progress (plan 2026-10-14-002 U0b(d)). The per-person rows
       * are NOT here any more (U0a): the Dispatch log pages them through
       * `GET …/sends/:dispatchId/log`, so this response stays one small object
       * per send however many people it reaches.
       */
      summary: PlaybooksSendSummarySchema,
    }),
  ),
  /**
   * TEST dispatches (U5), which have no `playbooks_play_send` row at all: a
   * test send runs the read half of `sendPlay` and never commits phase 1 (Key
   * Technical Decision 6), so `dispatch_id` — prefix `pbtest_<playId>_` — is
   * its ONLY handle. Hence a separate list rather than rows mixed into
   * `sends`: there is no send row to attach them to, and showing a test send
   * as a send is exactly the confusion the test path exists to avoid.
   *
   * `dispatchId` is non-nullable here (a real send row can predate PR D and
   * have none; a test send is nothing BUT its dispatch id), `sentAt` is the
   * earliest `dispatched_at` of the group, and `memberCount` is simply how
   * many rows the dispatch wrote — a test send records no roster size.
   */
  testSends: z.array(
    z.object({
      dispatchId: z.string().min(1),
      sentAt: z.string().min(1),
      memberCount: z.number().int().min(0),
      /**
       * The test's own `playbooks_play_send` row (plan 2026-10-14-002 U0b(g));
       * `null` for a test sent before that existed (found by its prefix only).
       */
      sendId: z.string().min(1).nullable(),
      /**
       * How many people the play routed when this test was SAMPLED down to
       * `memberCount` (plan 2026-10-15-005, `playbooks_play_send.routed_count`)
       * — the "of 571" in "100 of 571 people". `null` when the test took
       * everyone routed, for a chosen-people test, and for a legacy test found
       * by prefix. Required: web and API ship as one image.
       */
      routedCount: z.number().int().positive().nullable(),
      summary: PlaybooksSendSummarySchema,
    }),
  ),
});

/**
 * Query of `GET /v2/plays/:id/sends/:dispatchId/log` — one send's rows, paged
 * by row id (plan 2026-10-14-002 U0b(d)). `status` filters through the SAME
 * classifier the chips count with (`classifyRepStatus`), on the server, so a
 * chip's count and the rows it opens can never disagree. `after` is the last
 * row id of the previous page.
 */
export const PlaybooksSendLogPageQuerySchema = z.object({
  status: z.union([RepDispatchStatusSchema, z.literal('all')]).default('all'),
  after: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type PlaybooksSendLogPageQuery = z.infer<typeof PlaybooksSendLogPageQuerySchema>;

/**
 * Response of `GET /v2/plays/:id/sends/:dispatchId/log`. `nextAfter: null` = no
 * more rows. A FILTERED page may be short — even empty — with `nextAfter` set
 * (the server scans at most 5,000 rows per request): continue from it
 * (`useSendLogRows` does so on its own).
 */
export const PlaybooksSendLogPageResponseSchema = z.object({
  dispatchId: z.string().min(1),
  rows: z.array(PlaybooksDispatchLogRowSchema),
  nextAfter: z.string().min(1).nullable(),
});
export type PlaybooksSendLogPageResponse = z.infer<typeof PlaybooksSendLogPageResponseSchema>;

/**
 * The `dispatched_at` half of a {@link PlaybooksPlayLogPageQuerySchema} cursor:
 * UTC ISO with up to MICROsecond precision. The column is `TIMESTAMPTZ`
 * (µs) and one dispatch's rows share one `now()`, so a millisecond cursor
 * (`Date#toISOString`) would compare GREATER than every row it was cut from
 * and silently skip the rest of the send — the server mints the cursor from
 * the column's own µs text, never from a JS `Date`. Strict on purpose: the
 * value is cast to `timestamptz` in SQL, so anything else is a 400 here, not
 * a 500 there.
 */
const PLAY_LOG_CURSOR_AT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(\.\d{1,6})?Z$/;

/**
 * The shape alone lets `2026-99-99T99:99:99Z` through, which Postgres rejects
 * with a 500. Parse the whole-second part and check every field round-trips;
 * the caller keeps the ORIGINAL string (microseconds survive).
 */
function isRealPlayLogCursorInstant(at: string): boolean {
  const m = PLAY_LOG_CURSOR_AT.exec(at);
  if (m === null) return false;
  const [year, month, day, hour, minute, second] = m.slice(1, 7).map(Number) as [number, number, number, number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day &&
    date.getUTCHours() === hour &&
    date.getUTCMinutes() === minute &&
    date.getUTCSeconds() === second
  );
}

/** `"<dispatched_at ISO µs>|<row id>"` — the play log's keyset cursor (B1). */
export function encodePlayLogCursor(at: string, id: string): string {
  return `${at}|${id}`;
}

/**
 * Splits a play-log cursor on its FIRST `|` (an ISO time has none; a row id
 * may). `null` when either half is malformed — the query schema 400s it.
 */
export function decodePlayLogCursor(cursor: string): { at: string; id: string } | null {
  const bar = cursor.indexOf('|');
  if (bar < 0) return null;
  const at = cursor.slice(0, bar);
  const id = cursor.slice(bar + 1);
  if (id.length === 0 || !isRealPlayLogCursorInstant(at)) return null;
  return { at, id };
}

/**
 * Query of `GET /v2/plays/:id/log` (plan 2026-10-15-004 B1) — every person's
 * row across EVERY send of a play (tests and real, one-person and
 * whole-roster), newest first, keyset by `(dispatched_at, id) DESC`.
 *
 * `kind` narrows to test or real sends (a send's own `is_test`); `status`
 * filters through the ONE classifier exactly as the per-send page does;
 * `memberId` narrows to one person. `after` is the previous page's
 * `nextAfter` — the composite cursor, never shown to a rep.
 */
export const PlaybooksPlayLogPageQuerySchema = PlaybooksSendLogPageQuerySchema.extend({
  kind: z.enum(['all', 'test', 'real']).default('all'),
  memberId: z.string().min(1).optional(),
  after: z
    .string()
    .min(1)
    .refine((value) => decodePlayLogCursor(value) !== null, { message: 'Not a play log cursor.' })
    .optional(),
});
export type PlaybooksPlayLogPageQuery = z.infer<typeof PlaybooksPlayLogPageQuerySchema>;

/**
 * Response of `GET /v2/plays/:id/log`. The row is {@link PlaybooksDispatchLogRowSchema}
 * unchanged (`is_test`, `dispatched_at`, `dispatch_id`, `campaign_member_id`,
 * `bucket` are all on it). `nextAfter: null` = no more rows; a FILTERED page
 * may be short — even empty — with `nextAfter` set (same scan budget as the
 * per-send page): continue from it.
 */
export const PlaybooksPlayLogPageResponseSchema = z.object({
  rows: z.array(PlaybooksDispatchLogRowSchema),
  nextAfter: z.string().min(1).nullable(),
});
export type PlaybooksPlayLogPageResponse = z.infer<typeof PlaybooksPlayLogPageResponseSchema>;
export type PlaybooksPlaySendsResponse = z.infer<typeof PlaybooksPlaySendsResponseSchema>;

/**
 * Query of `GET /v2/plays/:id/dispatch-payload`: which member, and whether to
 * read the LOCK (`sendId` given — "what was posted") or the current heads
 * (absent — "what will be posted").
 */
export const PlaybooksDispatchPayloadQuerySchema = z.object({
  memberId: z.string().min(1),
  sendId: z.string().min(1).optional(),
});
export type PlaybooksDispatchPayloadQuery = z.infer<typeof PlaybooksDispatchPayloadQuerySchema>;

/**
 * Compile-time type EQUALITY (not mere assignability, which `satisfies`
 * gives): `Equal<A, B>` is `true` only when A and B are mutually assignable in
 * both directions, so a variant added to one side and not the other fails the
 * typecheck rather than silently widening. Used below to pin the two zod
 * MIRRORS of types that live in zod-free modules (`_shared`'s `EmptyFallback`,
 * `sequence-token-flags.ts`'s `TokenFlagKind`).
 *
 * The function-signature form is the standard trick: two identical conditional
 * types are only assignable to each other when their checked types are
 * identical, which plain `extends` cannot express.
 */
type Equal<A, B> =
  (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2 ? true : false;
/** Instantiating this with `false` is the compile error — see {@link Equal}. */
type AssertTrue<T extends true> = T;

/**
 * The zod mirror of `_shared/launcher-copy-tokens.ts`'s `EmptyFallback` — what
 * the resolver does with a KNOWN token this member has no value for
 * (Key Technical Decision 2): put `text` where the token was, or drop the
 * whole enclosing line/block.
 *
 * `_shared` stays zod-free (it is frontend-safe and its every change triggers
 * the core-review workflow), so the wire shape has to be declared here. The
 * two cannot drift: {@link EmptyFallbackMirrorsShared} fails the typecheck if
 * a variant or a field is added to either side alone.
 *
 * The report never restates the policy — the `fallback` on a token row is this
 * declaration, read straight off `COPY_VARIABLE_FIELDS`.
 */
export const EmptyFallbackSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('substitute'), text: z.string() }),
  z.object({ kind: z.literal('drop-line') }),
]);
export type EmptyFallbackMirrorsShared = AssertTrue<
  Equal<z.infer<typeof EmptyFallbackSchema>, EmptyFallback>
>;

/**
 * The zod mirror of `_shared`'s `AppliedFallback`: WHICH token's fallback
 * fired, and which kind it was. Pinned to the shared type the same way
 * {@link EmptyFallbackSchema} is, so the `kind` vocabulary is declared once.
 */
export const AppliedFallbackSchema = z.object({
  token: z.string().min(1),
  kind: z.enum(['substitute', 'drop-line']),
});
export type AppliedFallbackMirrorsShared = AssertTrue<
  Equal<z.infer<typeof AppliedFallbackSchema>, AppliedFallback>
>;

/**
 * The zod mirror of `sequence-token-flags.ts`'s `TokenFlagKind` — U2's one
 * classifier vocabulary, pinned the same way {@link EmptyFallbackSchema} is
 * (that module is pure and browser-safe, and imports no zod).
 */
export const TokenFlagKindSchema = z.enum([
  'gong_unmapped',
  'conditional',
  'unknown',
  // No `'company_name'`: `{{Company Name}}` is Clay-owned now and never
  // flagged — see `sequence-token-flags.ts`'s `TokenFlagKind`.
  // The five non-`{{}}` classes (playbooks-widen-copy-defect-detection) —
  // `sequence-token-flags.ts`'s `NonBraceDefectKind`.
  'single_brace',
  'double_bracket',
  'angle_bracket',
  'dead_href',
  'scaffolding',
]);
export type TokenFlagKindMirrorsClassifier = AssertTrue<
  Equal<z.infer<typeof TokenFlagKindSchema>, TokenFlagKind>
>;

/**
 * One member's resolution outcome on the wire (U5) — `send-payload.ts`'s
 * `MemberResolution`, minus the `unknownByField` split that only
 * `memberDefectError` consumes.
 *
 * TWO RULES, and the report's whole job is keeping them apart. An `empty`
 * token is a DATA gap: the value is missing, its declared `fallback` fired
 * (see `fallbacks`), and the member SENDS. An `unknown` token is a COPY
 * defect: nothing can resolve it, so that member is skipped with a `failed`
 * log row while every other member of the send still goes out (R11).
 * `defect` is, again, exactly `unknown.length > 0` (the binding product
 * ruling in playbooks-widen-copy-defect-detection) and is the only bit a
 * caller branches on.
 *
 * `steps` covers the AUTHORED steps only (the 20-entry package's blank pads
 * carry no resolution), and `position` is the step's own 0-based position —
 * the rep-facing label is `position + 1`.
 *
 * `unknown` (both the per-step and the folded list) is `z.array(z.string())`,
 * NOT `.min(1)` per entry (U4, Codex B1) — an unknown token's key may
 * legitimately be `''` (`{{}}`/`{{ }}`), and the wire must carry whatever the
 * resolver reports rather than reject it.
 *
 * `nonBraceDefects` mirrors `MemberResolution.nonBraceDefects` — the five
 * non-`{{}}` classes, folded across steps the same way `unknown` is. This is
 * now purely a WARNING list (playbooks-widen-copy-defect-detection): it is
 * REPORTED for every member who has it (preview, report, inspector) and does
 * NOT affect `defect`, which stays exactly `unknown.length > 0`. A member
 * whose only issue is non-brace copy still sends.
 *
 * `deferred` is the THIRD state (2026-09-16): a `CLAY_OWNED_TOKENS` member
 * left literally in the copy for Clay's `Render copy` node to fill. It is
 * neither of the two above — the member SENDS (so it is not a defect) and no
 * fallback fired (so it is not a gap) — which is why it is its own list and
 * why `defect` is unaffected by it. Entries ARE `.min(1)`: unlike `unknown`,
 * a deferred entry is always one of three declared, canonicalized names, so
 * an empty string here would be a bug rather than a rep's `{{}}`.
 *
 * `replySubjectsOmitted` (playbooks-widen-copy-defect-detection) mirrors
 * `MemberResolution.replySubjectsOmitted` — 1-based step numbers whose stored
 * subject was dropped because the target Gong flow treats that step as a
 * reply. `.default([])` for a client hitting an older server mid-deploy, the
 * same reason `nonBraceDefects` has one.
 */
export const PlaybooksMemberResolutionSchema = z.object({
  steps: z.array(
    z.object({
      position: z.number().int().nonnegative(),
      empty: z.array(z.string().min(1)),
      deferred: z.array(z.string().min(1)),
      unknown: z.array(z.string()),
      fallbacks: z.array(AppliedFallbackSchema),
      valueDefects: z.array(PlaybooksValueDefectSchema).default([]),
    }),
  ),
  empty: z.array(z.string().min(1)),
  deferred: z.array(z.string().min(1)),
  unknown: z.array(z.string()),
  nonBraceDefects: z.array(PlaybooksNonBraceDefectSchema).default([]),
  /**
   * U0f — values that were themselves placeholders; their fallback fired and
   * the member sends. A warning list like `nonBraceDefects`, never part of
   * `defect`. `.default([])` for a client hitting an older server mid-deploy.
   */
  valueDefects: z.array(PlaybooksValueDefectSchema).default([]),
  defect: z.boolean(),
  replySubjectsOmitted: z.array(z.number().int().positive()).default([]),
});
export type PlaybooksMemberResolution = z.infer<typeof PlaybooksMemberResolutionSchema>;

/**
 * Response of `GET /v2/plays/:id/dispatch-payload` — one member's Clay payload,
 * REBUILT by the same pure builder the send used (Key Technical Decision 11).
 *
 * `reconstructed` is a `z.literal(true)`, not a boolean: the log row stores
 * only step-0 `subject`/`body`, so there is no "read the real payload back"
 * mode this endpoint could ever answer with, and a caller must not be able to
 * treat a rebuild as a recording. `note` carries that caveat in words for the
 * UI caption — `dispatched_at` and every member field reflect NOW, so an
 * hourly SFDC sync between the send and the read shows up here as drift.
 */
export const PlaybooksDispatchPayloadResponseSchema = z.object({
  payload: z.record(z.string(), z.unknown()),
  /**
   * What resolving this member's copy turned up (U5) — the builder's OWN
   * `MemberResolution`, not a second reading of the payload. The preview and
   * the send call `buildPlaybooksMemberPayload` once each with the same
   * inputs, so `defect` here is exactly the bit that decides whether that
   * member dispatches.
   */
  resolution: PlaybooksMemberResolutionSchema,
  reconstructed: z.literal(true),
  note: z.string(),
});
export type PlaybooksDispatchPayloadResponse = z.infer<
  typeof PlaybooksDispatchPayloadResponseSchema
>;

// ─────────────────────────────────────────────────────────────────────────────
// Segmentation condition-version write (PR C, U3C) — R2
//
// The Qualify step's "Edit conditions" is a LIBRARY-OBJECT edit, not a
// playbook-draft edit (a rung only ever points at a segmentation id; the
// conditions live on the segmentation's own version history) — same
// `expectedVersionId`-guarded whole-version write as
// `PlaybooksSaveSequenceStepsRequestSchema`, applied to a segmentation's
// `conditions` field. `default_mapping` is deliberately NOT accepted here: it
// is a separate, still-unbuilt editing surface (Phase 1 default-mapping UI),
// so this endpoint carries the current head's `default_mapping` through
// unchanged rather than accepting a value the caller has no UI to set
// correctly.
// ─────────────────────────────────────────────────────────────────────────────

export const PlaybooksSaveSegmentationConditionsRequestSchema = z.object({
  conditions: SegmentationConditionsSchema,
  expectedVersionId: z.string().min(1),
  /**
   * Playbooks QA round 4, U6 (S2) / Decision 13 — how many PLACES (rungs
   * across every non-archived playbook's head ladder) the client showed the
   * rep before they approved this "Update all" save. Exact twin of
   * `PlaybooksSaveSequenceStepsRequestSchema.confirmedMappingCount`, including
   * its `.nonnegative()` (not `.positive()`) bound: a sole-use save sends the
   * fresh `places.length`, which is legitimately `0` for a segmentation no
   * ladder references yet. Absent → the handler defaults to `1`, the
   * no-dialog sole-use assumption.
   */
  confirmedPlaceCount: z.number().int().nonnegative().optional(),
  /**
   * Plan 2026-09-29-001 U1 (rulebook R3/R5/R14) — the group (rung) an
   * in-play save was made from; required whenever the request also carries
   * `?playId=` (`api-shared.ts#inPlayWriteGroup` 400s `group_required`
   * otherwise). Absent on a Library (master-doorway) save, which names no
   * play at all.
   */
  rungId: z.string().min(1).optional(),
  /**
   * The fan-out the rep was shown for a silent (sole-use) save — the exact
   * twin of `PlaybooksSaveSequenceStepsRequestSchema.confirmedRungIds`. See
   * that field's doc for the shared-mapping-conflict reasoning.
   */
  confirmedRungIds: z.array(z.string().min(1)).optional(),
});
export type PlaybooksSaveSegmentationConditionsRequest = z.infer<
  typeof PlaybooksSaveSegmentationConditionsRequestSchema
>;

/** Mirrors `PlaybooksSaveSequenceStepsResponseSchema`'s shape, applied to conditions. */
export const PlaybooksSaveSegmentationConditionsResponseSchema = z.object({
  id: z.string().min(1),
  versionId: z.string().min(1),
  versionNumber: z.number().int().nullable(),
  conditions: SegmentationConditionsSchema,
});
export type PlaybooksSaveSegmentationConditionsResponse = z.infer<
  typeof PlaybooksSaveSegmentationConditionsResponseSchema
>;

// ─────────────────────────────────────────────────────────────────────────────
// Sequence CRUD + step-version write + flow lock (PR C, U4A) — R3
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Body of `POST /v2/sequences` — the shared create shape plus `channel`.
 *
 * `channel` is required and has no default: it is identity-INVARIANT (it lives
 * on `playbooks_sequence`, not on a version — Key Decision 5), so it can only be
 * chosen at create and no later route can change it. Guessing a default here
 * would silently bind a sequence to the wrong send transport.
 *
 * `flowId` is deliberately NOT accepted at create. The flow binding has exactly
 * one owner — `PATCH /v2/sequences/:id/flow`, whose one-way guard is the whole
 * point (Decision 5) — and a second entry point would be a second place that
 * guard has to be restated.
 */
export const PlaybooksCreateSequenceRequestSchema = PlaybooksCreateIdentityRequestSchema.extend({
  channel: LauncherChannelSchema,
  /**
   * Playbooks QA round 4, U9 (SN1) / Decision 8 — the sender to lock this
   * sequence to, collected by `SequenceCreateFields` at create.
   *
   * Optional-AND-nullable, not required: "Not set until edited" is the
   * documented state for a sequence with no sender (Decision 10), and the
   * Gong-import doorway (`createSequenceFromGongFlow`) legitimately creates
   * sequences whose sender the rep hasn't chosen yet. The format check is the
   * handler's (`isSenderToken(v) || isValidSenderEmail(v)` → 400) rather than
   * the schema's, for the same reason the mapping schema leaves it free — see
   * `sender-choice.ts`'s "WHY THE FORMAT CHECK LIVES HERE" note.
   */
  sender: z.string().nullable().optional(),
});
export type PlaybooksCreateSequenceRequest = z.infer<typeof PlaybooksCreateSequenceRequestSchema>;

/**
 * Body of `POST /v2/sequences/:id/versions` — a whole-snapshot copy save.
 *
 * `steps` is the COMPLETE step array, never a delta: a sequence version is
 * self-contained by design (Key Decision 12), which is what lets Phase 2 diff
 * any two versions without replaying edits. An empty array is legal and means
 * exactly what it says — a sequence whose copy has been cleared — matching
 * `SequenceStepsSchema`'s own documented "empty is legal".
 *
 * `expectedVersionId` carries the identical contract (and identical lost-update
 * reasoning) as `PlaybooksReorderRungsRequestSchema`'s and the playbook save's:
 * the handler reads head once outside a transaction, then writes in a separate
 * `createVersion` call, so the caller must prove which head its copy came from.
 * Required, not optional.
 *
 * `confirmedMappingCount` is the shared-save acknowledgment (round-2-followup
 * plan, Unit B2, decision 1): the count of active rung mappings the caller
 * displayed to the rep before this save (e.g. from the gate dialog), or
 * omitted entirely for the no-dialog sole-use save path (the handler then
 * defaults to `1`). The handler recomputes the actual count immediately
 * before writing and refuses with `shared_mapping_conflict` (409) if it now
 * exceeds what was acknowledged — closing the TOCTOU window between the
 * client's stale read and this write, though only down to the milliseconds
 * between that recount and the write itself (see the plan doc's decision 1
 * for why full cross-aggregate serialization is out of scope here).
 *
 * `.nonnegative()`, not `.positive()` (round-3-QA-fixes plan, Codex
 * adversarial-review finding F1): both save gates now always send
 * `places.length` from the fresh usage fetch, and an unused sequence
 * legitimately has ZERO places. Rejecting `0` at the schema layer 400'd
 * every zero-place save before the handler's own `actualMappingCount >
 * (body.confirmedMappingCount ?? 1)` check ever ran — with `0` acknowledged
 * and `0` actual, that check's semantics are unchanged and the save
 * proceeds; the fix is purely at this validation boundary.
 */
export const PlaybooksSaveSequenceStepsRequestSchema = z.object({
  steps: SequenceStepsSchema,
  expectedVersionId: z.string().min(1),
  confirmedMappingCount: z.number().int().nonnegative().optional(),
  /**
   * Round-4 QA fixes, U5 (S1) / Decision 6 — the UNSENT-PLAY counterpart of
   * `confirmedMappingCount`, same optionality and the same `?? 1` no-dialog
   * default: the count of distinct-playbook unsent plays
   * (`usage-gate.ts#distinctUnsentPlays`) the caller displayed to the rep
   * before this save, or omitted for the sole-use path. The handler
   * recomputes `countUnsentPlaysForSequence` immediately before writing and
   * refuses with the SAME `shared_mapping_conflict` (409) if it now exceeds
   * this — one error code for both axes of the gate, since the client's
   * bounded retry (re-fetch usage, try once more) already handles either.
   */
  confirmedUnsentPlayCount: z.number().int().nonnegative().optional(),
  /**
   * U7a — carries the "Generate with AI" provenance that used to be stamped
   * only by the now-doomed confirm endpoint, so the ORDINARY save-steps write
   * can persist `generation_inputs` / derive `generation_preset` once AI
   * Apply (U7b) routes through it. Optional and additive: every existing
   * caller that omits it keeps behaving exactly as before. `null` is not
   * accepted — omission is the only "no generation" spelling.
   */
  generationInputs: PlaybooksGenerationInputsSchema.optional(),
  /**
   * Plan 2026-09-29-001 U1 (rulebook R3/R5/R14) — the group (rung) an
   * in-play save was made from; required whenever the request also carries
   * `?playId=` (`api-shared.ts#inPlayWriteGroup` 400s `group_required`
   * otherwise). Absent on a Library (master-doorway) save.
   */
  rungId: z.string().min(1).optional(),
  /**
   * The fan-out (every group also sharing this sequence) the rep was shown
   * before a silent (sole-use) save — checked against the LOCKED ladder
   * (`assertGroupsNameIdentityWithin`) so a group that started sharing it
   * between the usage read and this write is refused
   * `shared_mapping_conflict` rather than silently overwritten.
   */
  confirmedRungIds: z.array(z.string().min(1)).optional(),
});
export type PlaybooksSaveSequenceStepsRequest = z.infer<
  typeof PlaybooksSaveSequenceStepsRequestSchema
>;

/**
 * Response of `POST /v2/sequences/:id/versions` — the new main-line version's
 * identifiers plus the steps EXACTLY as persisted, so the client can adopt the
 * new `expectedVersionId` without a re-fetch. Mirrors
 * `PlaybooksSavePlaybookRungsResponseSchema` field-for-field minus the
 * apply-to-campaigns leg, which is a playbook-only concept.
 */
export const PlaybooksSaveSequenceStepsResponseSchema = z.object({
  id: z.string().min(1),
  versionId: z.string().min(1),
  versionNumber: z.number().int().nullable(),
  steps: SequenceStepsSchema,
});
export type PlaybooksSaveSequenceStepsResponse = z.infer<
  typeof PlaybooksSaveSequenceStepsResponseSchema
>;

/**
 * Body of `POST /plays/:id/sequences/:sequenceId/copy` — the play-scoped
 * counterpart of `PlaybooksSaveSequenceStepsRequestSchema` (plan
 * plans/2026-09-24-005-playbooks-skill-play-scoped-copy-plan.md, Key
 * Technical Decision 6). No `confirmedMappingCount`/`confirmedUnsentPlayCount`
 * — a play save only ever touches that one play, so the shared-save gate
 * those exist for does not apply here.
 *
 * `expectedVersionId` is OPTIONAL optimistic concurrency, unlike the template
 * save's REQUIRED field: omitted means "last write wins" (the machine caller's
 * re-run path — R8); a non-empty string must equal the head of the ADDRESSED
 * sequence (the outline's `sequences[].headVersionId`) or the save 409s with
 * `version_conflict`. There is no `null` form (plan 2026-09-25-002, Decision
 * 7): every sequence a play maps has a head, so a `null` is a 400 schema
 * error. The current contract (plan 2026-09-27-006 U3.5, Key Technical
 * Decisions 4, 10):
 *  - the posted steps' positions are checked against the addressed
 *    sequence's head (`stepPositionMismatchDetail`);
 *  - addressed to a sequence the play already OWNS, the save writes that copy
 *    directly;
 *  - addressed to a LIBRARY sequence the play maps, the first save copies it
 *    into the play (the response's `forkedFrom` / `ladder` say so). The quote
 *    is translated ONLY on that fresh copy — the copy's v1 equals the Library
 *    head the caller validated against. If the play already has a copy of it,
 *    a Library-head quote is refused `version_conflict` ("already copied into
 *    this play by another editor") and the caller reloads the play.
 * The play flow itself always sends it; the chat skill may omit it.
 * `.min(1)` on the string form for the same reason
 * `PlaybooksDuplicateSequenceRequestSchema.expectedSourceVersionId` uses it —
 * a blank string could never equal a real id and would silently read as "no
 * guard" if accepted.
 *
 * `generationInputs` is additive, same optional-not-nullable convention as
 * `PlaybooksSaveSequenceStepsRequestSchema.generationInputs`: the play-flow
 * "Apply to sequence" path already carries it; the chat skill omits it.
 *
 * `steps` is refined to non-empty HERE, on the request only — the shared
 * `SequenceStepsSchema` deliberately allows `[]` (an identity can exist
 * before any copy is written). `steps: []` on THIS save would pass the
 * position check against an empty parent (S3) and write a zero-step version
 * on the play's own sequence, trapping the play on an empty copy.
 *
 * `name` is optional (plan 2026-10-01-001, rulebook R17/R19): when sent, the
 * save also titles the play-owned copy it writes, in the same transaction as
 * the steps. It never renames a Library sequence, the playbook, or another
 * play's copy. The chat skill sends it; the play flow never does.
 */
export const PlaybooksSavePlaySequenceCopyRequestSchema = z.object({
  steps: SequenceStepsSchema.refine(
    (steps) => steps.length > 0,
    'Play copy must include at least one step.',
  ),
  expectedVersionId: z.string().min(1).optional(),
  generationInputs: PlaybooksGenerationInputsSchema.optional(),
  /**
   * The group this save is for (plan 2026-09-28-006 U1, rulebook R3/R17). The
   * play flow sends `rungId` and `mode` on every save — a group-scoped copy;
   * the chat skill sends neither — every group still on the addressed Library
   * sequence moves to ONE new copy, groups that already have their own copy
   * are left alone (reported in `skippedRungIds`). Under the play lock the
   * group must still name the addressed sequence, else `version_conflict`
   * (R14 — the rep's view of the play is stale).
   */
  rungId: z.string().min(1).optional(),
  /**
   * `'ensure'` (default): an owned sequence is written in place, a Library
   * one is copied for `rungId` first. `'fork'`: "Save as new — this group
   * only" — always a new copy for `rungId`, even of a copy this play already
   * owns. An explicit `mode` REQUIRES `rungId` (plan 2026-09-28-007 U3, R3):
   * refused below, so a `'fork'` can never fall through to "every group".
   */
  mode: z.enum(['ensure', 'fork']).optional(),
  /**
   * "Update N groups in this play" on an owned copy (plan 2026-09-28-007 U3,
   * R8): the groups of THIS play the rep was shown sharing the copy
   * (`usage-sidebar-logic.ts#confirmedFanOutSet`, this play's entry). Under
   * the play lock it must equal the set of groups naming the addressed
   * sequence, else `shared_mapping_conflict`; on success `movedRungIds`
   * echoes that set so the toast counts from the response. The chat skill
   * never sends it.
   */
  confirmedRungIds: z.array(z.string().min(1)).min(1).optional(),
  /**
   * Title for the play's OWN copy this save writes to (plan 2026-10-01-001,
   * R17/R19) — never a Library sequence, the playbook, or another play's copy.
   * Trimmed, 1–200 characters: the 200 cap is a deliberate machine-input cap
   * (plan Decision 3; the session rename has none). The UI never sends it.
   */
  name: z.string().trim().min(1).max(200).optional(),
}).superRefine((body, ctx) => {
  if (body.mode !== undefined && body.rungId === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['rungId'], message: 'rungId is required with mode.' });
  }
});
export type PlaybooksSavePlaySequenceCopyRequest = z.infer<
  typeof PlaybooksSavePlaySequenceCopyRequestSchema
>;

/**
 * Response of `POST /plays/:id/sequences/:sequenceId/copy` — the new copy
 * row's identifiers plus the steps exactly as persisted (Decision 6), and
 * `playUrl` (Decision 11, R6) so the caller — the chat skill has no browser
 * session of its own — can share a direct link to the play without knowing
 * the URL shape itself. `source` is always the literal `'play'`: this
 * endpoint never writes a template row, so there is no other value it could
 * report.
 *
 * Copy-on-write (plan 2026-09-27-006 U3.5, Key Technical Decision 10):
 * `sequenceId` is always a sequence the play OWNS. When the caller addressed a
 * Library sequence the play's ladder maps, this save copied it into the play
 * first — `forkedFrom` is the id the caller addressed and `ladder` the
 * play's rewritten ladder (the client adopts it; a machine caller reads the
 * outline again). Both are `null` when the save landed on an existing copy.
 *
 * `movedRungIds` / `skippedRungIds` (plan 2026-09-28-006 U1 (f), rulebook
 * R17 / edge 9): the groups this save moved onto the new copy, and — on a
 * save with no `rungId` — the groups left alone because they already had
 * their own version of the addressed Library sequence. Both `[]` when the save
 * wrote an owned sequence in place. Ids only: join to the outline's
 * `buckets[].rungId` for names, and tell the user which groups did not change
 * because they already had their own version.
 *
 * `name` (plan 2026-10-01-001) is present only when the request carried one.
 */
export const PlaybooksSavePlaySequenceCopyResponseSchema = z.object({
  playId: z.string(),
  sequenceId: z.string(),
  versionId: z.string(),
  steps: SequenceStepsSchema,
  source: z.literal('play'),
  playUrl: z.string().url(),
  forkedFrom: z.string().nullable(),
  ladder: PlaybooksPlayLadderRewriteSchema.nullable(),
  movedRungIds: z.array(z.string()),
  skippedRungIds: z.array(z.string()),
  /** The copy's name as written; present only when the request carried `name`. */
  name: z.string().optional(),
});
export type PlaybooksSavePlaySequenceCopyResponse = z.infer<
  typeof PlaybooksSavePlaySequenceCopyResponseSchema
>;

/**
 * Body of `POST /v2/sequences/:id/duplicate` (SEQUENCE kind only —
 * segmentation/playbook duplicates take no body at all). `steps` is optional:
 * a bodyless POST (every caller before round-2-followup Unit B2, and every
 * caller of the plain "Duplicate" kebab action today) keeps seeding the new
 * identity's v1 from the source's current head steps, unchanged. When present,
 * `steps` seeds the new identity's v1 with THOSE steps instead — this is what
 * makes "duplicate, then save an edited copy onto it" one atomic server
 * operation instead of two requests that could partially fail (round-2-followup
 * plan, Unit B2, decision 3).
 *
 * `generationInputs` (U17, F6) carries the same "Generate with AI" provenance
 * `PlaybooksSaveSequenceStepsRequestSchema.generationInputs` does, for the
 * "Save a new version" usage-sidebar decision — same optional-not-nullable
 * convention: omission is the only "no generation" spelling, `null` is
 * rejected.
 *
 * `expectedSourceVersionId` (U19, M1) is the head of the SOURCE identity that
 * the caller's `steps` were edited or generated AGAINST. When present, the
 * server re-reads the source head inside the same transaction that creates the
 * copy and refuses with `version_conflict` (409) if it has moved — without it,
 * "Save a new version" would fork a NEW identity carrying the source's
 * current-but-unseen flow/channel binding while seeding it with steps derived
 * from a head someone else has already replaced (Codex round-2 "Might Break"
 * #1: stale generated copy + a newly rebound Gong flow in one new identity).
 * Optional, and optional-not-nullable for the same reason
 * `generationInputs` is: the plain Config "Duplicate" kebab
 * (`mutations.ts#duplicateSequence(apiFetch, id)`) copies whatever the live
 * head is BY DEFINITION and has nothing stale to guard, so it omits the field;
 * both "Save a new version" hosts always send it. `.min(1)` because a blank id
 * could never equal a real head — it would be a caller bug that silently read
 * as "no guard".
 */
export const PlaybooksDuplicateSequenceRequestSchema = z.object({
  steps: SequenceStepsSchema.optional(),
  generationInputs: PlaybooksGenerationInputsSchema.optional(),
  expectedSourceVersionId: z.string().min(1).optional(),
  /**
   * Playbooks QA round 6, U5/U8 (item 3) — overrides the copy's
   * `default_sender` instead of carrying over the source's. Omitted (the
   * default for every pre-round-6 caller) means "carry the source's sender",
   * unchanged; `null` explicitly forks with "Not set". Format check is the
   * handler's own `validatedSender` (`api.ts`), mirroring
   * `PlaybooksCreateSequenceRequestSchema.sender` — see that field's doc.
   */
  sender: z.string().nullable().optional(),
  /** The play the copy belongs to (plan 2026-09-25-002, Decision 13); see `OwningPlayIdSchema`. */
  owningPlayId: OwningPlayIdSchema,
});
export type PlaybooksDuplicateSequenceRequest = z.infer<
  typeof PlaybooksDuplicateSequenceRequestSchema
>;

/**
 * Body of `POST /v2/segmentations/:id/duplicate` — Playbooks QA round 4, U6
 * (S2) / Decision 13. The SEGMENTATION analogue of
 * {@link PlaybooksDuplicateSequenceRequestSchema}: `conditions` is to a
 * segmentation fork what `steps` is to a sequence fork, and
 * `expectedSourceVersionId` carries the identical compare-and-set contract
 * (the source head the rep's draft was edited against; a head that moved
 * since then answers 409 `version_conflict` having written nothing).
 *
 * Both optional, for the same reason the sequence schema's fields are: the
 * plain Config "Duplicate" kebab (`mutations.ts#duplicateSegmentation(apiFetch,
 * id)`) copies whatever the live head is BY DEFINITION and sends no body at
 * all, so omitting both must stay byte-identical to the pre-U6 behavior.
 * Only the usage sidebar's "Save a new version" sends them.
 *
 * `default_mapping` is deliberately NOT accepted, exactly as
 * {@link PlaybooksSaveSegmentationConditionsRequestSchema} does not accept it:
 * there is no UI to set it correctly, so the fork carries the SOURCE head's
 * value through unchanged. That is a documented asymmetry of the fork (draft
 * conditions + source mapping), not an oversight — see `duplicateEndpoint`'s
 * own comment.
 */
export const PlaybooksDuplicateSegmentationRequestSchema = z.object({
  conditions: SegmentationConditionsSchema.optional(),
  expectedSourceVersionId: z.string().min(1).optional(),
  /** The play the copy belongs to (plan 2026-09-25-002, Decision 13); see `OwningPlayIdSchema`. */
  owningPlayId: OwningPlayIdSchema,
});
export type PlaybooksDuplicateSegmentationRequest = z.infer<
  typeof PlaybooksDuplicateSegmentationRequestSchema
>;

/**
 * Body of `PATCH /v2/sequences/:id/flow` — the flow to bind (or REBIND onto,
 * Batch 6 D4), and nothing else.
 *
 * There is still no `flowId: null` to unset — the only legal body is a
 * concrete flow id, which is why `flowId` is `.min(1)`-non-nullable rather
 * than nullable-optional. Re-pointing a sequence at a different flow no
 * longer requires creating a new Sequence identity (D4 repealed that
 * one-way lock): the same shape now also means "rebind", with the caller
 * responsible for re-importing that flow's steps in the same action.
 *
 * A Gong sequence must still name a catalog flow (the handler checks via
 * `isGongFlowInCatalog`); every OTHER channel accepts a free-text id — that
 * channel's own sequence/flow identifier, as launcher's paste field always
 * took (playbooks-inflection-wiring, 2026-09-17) — because
 * `sequence-readiness.ts` requires a flow on every channel and there is no
 * Inflection / Co-Pilot catalog to pick from. `.trim().min(1)` on every
 * channel: still no `null`/unset, and a whitespace-only id is not a real one.
 *
 * No `flowName`: `playbooks_sequence` has no column for it (only `flow_id` —
 * `prisma/schema/launcher.prisma:90`), and the picker already resolves display
 * names from the live Gong browse response. Accepting a name the server cannot
 * store would be a phantom field.
 */
export const PlaybooksSetSequenceFlowRequestSchema = z.object({
  flowId: z.string().trim().min(1),
  /**
   * The group this OWNED in-play write is for (plan 2026-09-28-008 U3,
   * rulebook R5/R8/R14): only legal alongside `?playId=`. Under the play
   * lock the group must still name the addressed sequence, else
   * `version_conflict`; `confirmedRungIds` (the groups of THIS play the rep
   * was shown sharing it) must equal the current set, else
   * `shared_mapping_conflict`. Omitted = unchanged (Library / doorway
   * writes).
   */
  rungId: z.string().min(1).optional(),
  confirmedRungIds: z.array(z.string().min(1)).min(1).optional(),
});
export type PlaybooksSetSequenceFlowRequest = z.infer<
  typeof PlaybooksSetSequenceFlowRequestSchema
>;

/**
 * Response of `PATCH /v2/sequences/:id/flow` — the binding now in force. Same
 * on a first bind and on an idempotent re-bind of the same flow: the client's
 * locked-state render depends on the binding, not on whether this particular
 * call was the one that wrote it.
 */
export const PlaybooksSetSequenceFlowResponseSchema = z.object({
  id: z.string().min(1),
  flowId: z.string().min(1),
});
export type PlaybooksSetSequenceFlowResponse = z.infer<
  typeof PlaybooksSetSequenceFlowResponseSchema
>;

/**
 * Body of `PATCH /v2/sequences/:id/sender` — Playbooks QA round 4, U9 (SN1) /
 * Decision 8.
 *
 * NULLABLE, unlike its `flowId` sibling above, and that asymmetry is the whole
 * point: a flow bind has no unset path by design (one-way-ish, always onto
 * another catalog-valid flow), whereas a sender is freely re-editable and
 * clearable — `null` means "back to Not set". Required (not optional): the
 * only legal bodies are a concrete sender or an explicit `null`, so an empty
 * `{}` is a caller bug rather than a silent no-op.
 *
 * Format validation is the handler's, not this schema's — see
 * `PlaybooksCreateSequenceRequestSchema.sender`.
 */
export const PlaybooksSetSequenceSenderRequestSchema = z.object({
  sender: z.string().nullable(),
  /** See `PlaybooksSetSequenceFlowRequestSchema.rungId`'s own doc — identical contract. */
  rungId: z.string().min(1).optional(),
  confirmedRungIds: z.array(z.string().min(1)).min(1).optional(),
});
export type PlaybooksSetSequenceSenderRequest = z.infer<
  typeof PlaybooksSetSequenceSenderRequestSchema
>;

/** Response of `PATCH /v2/sequences/:id/sender` — the sender now in force. */
export const PlaybooksSetSequenceSenderResponseSchema = z.object({
  id: z.string().min(1),
  sender: z.string().nullable(),
});
export type PlaybooksSetSequenceSenderResponse = z.infer<
  typeof PlaybooksSetSequenceSenderResponseSchema
>;

/**
 * Body of `POST /plays/:id/sequences/:sequenceId/sender`
 * (`play-sender-api.ts#setPlaySequenceSenderEndpoint`, plan 2026-10-30-001
 * U2; rulebook R3/R14/R17) — the Inflection sender on a play's OWN sequence
 * copy, reachable from the machine surface.
 *
 * Derived from {@link PlaybooksSetSequenceSenderRequestSchema}, with one
 * deliberate narrowing: `sender` is a non-empty string, so `null` is refused
 * (plan Key Technical Decision 5) — clearing would make an Inflection play
 * fall back to the template default, the inconsistency the skill exists to
 * remove. The session `PATCH /sequences/:id/sender` keeps `null` = clear.
 *
 * `rungId` / `confirmedRungIds` stay optional (Decision 4): with no `rungId`
 * the write applies to the play's copy as a whole (a copy no group uses is
 * refused); with one, the same stale-group check a hand edit runs (R14).
 * Format validation stays at the handler (`validatedSender`). The response
 * reuses {@link PlaybooksSetSequenceSenderResponseSchema} unchanged.
 */
export const PlaybooksSetPlaySequenceSenderRequestSchema = PlaybooksSetSequenceSenderRequestSchema.extend({
  sender: z.string().min(1),
}).superRefine((body, ctx) => {
  // Same rule as the copy save's `mode`: a `confirmedRungIds` with no
  // `rungId` would otherwise be silently dropped and skip the R14 check.
  if (body.confirmedRungIds !== undefined && body.rungId === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['rungId'], message: 'rungId is required with confirmedRungIds.' });
  }
});
export type PlaybooksSetPlaySequenceSenderRequest = z.infer<
  typeof PlaybooksSetPlaySequenceSenderRequestSchema
>;

/**
 * Body of `PATCH /v2/sequences/:id/inflection` — the two Inflection-only
 * identity fields, written together (one popover edits both). BOTH keys are
 * required-and-nullable, mirroring `PlaybooksSetSequenceSenderRequestSchema`:
 * `null` clears a field back to "none", and an omitted key is a caller bug
 * (400) rather than a silent "leave it alone". Format validation (owner token
 * | valid email) is the handler's, via the same `validatedSenderLike` gate
 * `default_sender` uses — one vocabulary, three doorways.
 *
 * Refused with 400 `inflection_only` on any other channel: the payload
 * builder emits `cc_email`/`reply_to_email` as `null` there regardless, so a
 * stored value would be a phantom the UI could never explain.
 */
export const PlaybooksSetSequenceInflectionFieldsRequestSchema = z.object({
  cc: z.string().nullable(),
  replyTo: z.string().nullable(),
  /** See `PlaybooksSetSequenceFlowRequestSchema.rungId`'s own doc — identical contract. */
  rungId: z.string().min(1).optional(),
  confirmedRungIds: z.array(z.string().min(1)).min(1).optional(),
});
export type PlaybooksSetSequenceInflectionFieldsRequest = z.infer<
  typeof PlaybooksSetSequenceInflectionFieldsRequestSchema
>;

/** Response of `PATCH /v2/sequences/:id/inflection` — the values now in force. */
export const PlaybooksSetSequenceInflectionFieldsResponseSchema = z.object({
  id: z.string().min(1),
  cc: z.string().nullable(),
  replyTo: z.string().nullable(),
});
export type PlaybooksSetSequenceInflectionFieldsResponse = z.infer<
  typeof PlaybooksSetSequenceInflectionFieldsResponseSchema
>;

// ─────────────────────────────────────────────────────────────────────────────
// AI copy generation (PR C QA follow-ups, U12A) — R16
//
// `POST /v2/sequences/:id/generate-copy`. Writes NOTHING: it returns a
// generated draft the rep diffs against the saved head and then accepts
// through the existing `POST /v2/sequences/:id/versions` path (U12B's UI).
//
// Corrected comment (fix for a stale claim this comment used to make, PR C QA
// follow-ups): `headVersionId` does NOT become the eventual save's
// `expectedVersionId`. `SequencesStep.tsx`'s `handleAcceptGenerated` seeds the
// accepted draft against the CURRENT cached head at accept time and drops
// `headVersionId` entirely; `handleSaveStep` sends the CURRENT head's
// `versionId`, never the generation-time one — see `generated-copy-store.ts`'s
// own module doc ("the one generation-time fact worth keeping ... is used only
// to TELL the rep at Accept time that the head moved under them — it never
// becomes the save's `expectedVersionId`"). What actually ships:
// `headVersionId` only drives `CopyDiffView`'s `isHeadStale` warning ("This
// Sequence was saved by someone else since you pressed Generate"), shown
// BEFORE the rep accepts, non-blocking. A genuinely stale save is still caught
// by the real 409 `version_conflict` guard on the write itself (unrelated to
// this field) — the diff, not this id, is what tells the rep in advance.
//
// The three optional request fields are the prototype's Generate module inputs
// (`playbooks-prototype-v6.html`, `generateModule()` :3780) — a rep-editable
// campaign description, optional pasted source material, and an optional
// chosen list of Campaign Member statuses. Optional rather than required
// because the server has a real source for each absence: the campaign row's own
// `description`, "no source material", and "no status filter" respectively —
// none of them a guessed default.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Either strategy `generateSequenceCopy` can run — the SAME two-value enum
 * `copy-generation.ts`'s `GenerationStrategy` type derives from (U4b), not a
 * parallel union (`terra/no-parallel-schema-and-type`).
 */
export const PlaybooksGenerationStrategySchema = z.enum(['whole', 'per-step']);
export type PlaybooksGenerationStrategy = z.infer<typeof PlaybooksGenerationStrategySchema>;

export const PlaybooksGenerateSequenceCopyRequestSchema = z.object({
  campaignSfdcId: z.string().min(1),
  /**
   * The rep's (possibly edited) campaign description. Omitted → the server
   * uses `launcher_sfdc_campaigns.description`, which is what the UI seeds the
   * textarea from in the first place (prototype `:8529`, `:8604`).
   */
  campaignDescription: z.string().optional(),
  /** Rep-pasted extra context. Omitted → the prompt says "(none provided)". */
  sourceMaterial: z.string().optional(),
  /** Chosen `cm_status` values. Omitted/empty → no status filter. */
  memberStatuses: z.array(z.string().min(1)).optional(),
  /**
   * The rep's brief for this run (U4b). Omitted → `generateSequenceCopy`
   * defaults it to `DEFAULT_GENERATION_INSTRUCTION` — the same fallback the
   * module already applies for any caller that omits it, so making this
   * optional rather than required keeps `SequencesStep.tsx`'s existing
   * `handleGenerate` call site (which sends only `campaignSfdcId` today)
   * working unchanged until U7 replaces it with the configured drawer.
   */
  instruction: z.string().optional(),
  /**
   * Human-readable event name for the prompt's `## Event name` section
   * (U4b). Omitted → `generateSequenceCopy` defaults it to
   * `readableEventName(campaign.name)`, same reasoning as `instruction`
   * above.
   */
  eventName: z.string().min(1).optional(),
  /** Which generation strategy to run. Omitted → `DEFAULT_GENERATION_STRATEGY`. */
  strategy: PlaybooksGenerationStrategySchema.optional(),
  /**
   * An unused optional override point for the step scaffold to rewrite —
   * corrected comment (PR C QA follow-ups): this used to describe a
   * Gong-vs-non-Gong scaffold-selection rule as something a caller follows,
   * but no shipped caller sends this field at all. `SequencesStep.tsx`'s
   * `handleGenerate` never passes `baseSteps`, so in practice every request
   * falls back to the server reading the sequence's saved head steps. The
   * prototype's `composeGeneratedSteps()` (:3749) base rule this field was
   * modeled on — a Gong-backed sequence rewrites from the flow's own default
   * steps, a non-Gong one from its current copy — is not wired up anywhere and
   * is out of scope for this unit (the plan of record's U12 section does not
   * require it). The field stays for a future caller that wants to rewrite
   * from something other than the saved head; a sequence with neither a
   * client-supplied `baseSteps` nor a saved head is refused rather than having
   * a cadence invented for it.
   */
  baseSteps: SequenceStepsSchema.optional(),
});
export type PlaybooksGenerateSequenceCopyRequest = z.infer<
  typeof PlaybooksGenerateSequenceCopyRequestSchema
>;

/**
 * Response of `POST /v2/sequences/:id/generate-copy` — the generated draft plus
 * the head it was generated against.
 *
 * `steps` is a full `SequenceSteps` snapshot (not a per-step delta), so it can
 * be handed to `POST /v2/sequences/:id/versions` unchanged once the rep
 * accepts. Every step comes back `copyStatus: 'draft'` — generated copy is
 * unreviewed by definition (see `copy-generation.ts`'s
 * `generatedStepsToSequenceSteps`).
 *
 * `headVersionId` is nullable only because `GET /v2/sequences/:id` models a
 * head-less identity as legal; in practice `POST /v2/sequences` always mints a
 * v1, so a null here means the sequence had no saved version to diff against
 * at generation time. It is used only to drive `CopyDiffView`'s `isHeadStale`
 * warning at accept time (see the request schema's corrected comment above) —
 * it never becomes an `expectedVersionId` on the accept path; the accept save
 * always sends the head's CURRENT `versionId`, read fresh at save time.
 */
export const PlaybooksGenerateSequenceCopyResponseSchema = z.object({
  id: z.string().min(1),
  headVersionId: z.string().min(1).nullable(),
  headVersionNumber: z.number().int().nullable(),
  steps: SequenceStepsSchema,
  /** Whether `campaignDescription` was thin enough to warn the rep (`isThinDescription`, U4b). */
  thinContext: z.boolean(),
  /** Wall-clock ms the generation call took — surfaced for the harness measurement (U4b, §3c). */
  timingMs: z.number().nonnegative(),
  /** Which strategy actually ran (the request's `strategy`, or the default it fell back to) — including the server's own whole→per-step fallback (2026-09-17 QA follow-ups, U2). */
  strategy: PlaybooksGenerationStrategySchema,
});
export type PlaybooksGenerateSequenceCopyResponse = z.infer<
  typeof PlaybooksGenerateSequenceCopyResponseSchema
>;

/**
 * One frame of `POST /v2/sequences/:id/generate-copy`'s SSE stream (U4b) —
 * written by `openEventStream` (`_shared/event-stream.ts`) and validated on the client by
 * `streamSequenceCopyGeneration` (`./api-client.ts`) via `safeParse`. A frame
 * that fails to parse is skipped, never thrown (Codex-challenge resolution).
 *
 * `step` carries a full `SequenceStepSchema` object — the same shape
 * `generateSequenceCopy`'s `onStep` callback already hands the caller
 * (`{ position, subject, body }`, a strict subset of `SequenceStep`'s
 * optional fields). `done` mirrors the JSON response's three new fields plus
 * `headVersionId`. `error` carries a rep-facing `detail` string — the SAME
 * text the non-streaming JSON path's 502 `detail` would have carried.
 */
export const PlaybooksGenerateProgressEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('step'), step: SequenceStepSchema }),
  z.object({
    type: z.literal('done'),
    headVersionId: z.string().min(1).nullable(),
    thinContext: z.boolean(),
    timingMs: z.number().nonnegative(),
    strategy: PlaybooksGenerationStrategySchema,
    /**
     * Every scaffold position the server generated for (D5) — the client's
     * `finishCopyGeneration` refuses the `'ready'` transition unless each of
     * these already has a staged step, so a JSON-fallback dispatch that
     * stalls partway, or a dropped SSE `step` frame, surfaces as an error
     * instead of a silently-incomplete review.
     */
    positions: z.array(z.number().int()),
  }),
  z.object({ type: z.literal('error'), detail: z.string() }),
]);
export type PlaybooksGenerateProgressEvent = z.infer<typeof PlaybooksGenerateProgressEventSchema>;

/**
 * One step of `GET /plays/:id/outline`'s `sequences[].steps` (plan
 * 2026-09-25-002, Key Technical Decision 18) — the skill-facing view of a
 * `SequenceStepSchema`. Every key is present (nullable, not optional) so a
 * machine caller never has to tell "absent" from "empty":
 *   - `stepType`: `step-content-rules.ts#gongStepTypeLabel` of the step's Gong
 *     descriptor ("Manual Email", "Call", …); `null` for a step without a
 *     descriptor, whose type is the channel's own message.
 *   - `blankAllowed`: `true` only for a step with a Gong descriptor that is not
 *     an email step — the one blank the copy save accepts
 *     (`validateGongEngageSteps`).
 */
export const PlaybooksPlayOutlineStepSchema = z.object({
  position: z.number().int().nonnegative(),
  stepType: z.string().min(1).nullable(),
  subject: z.string().nullable(),
  body: z.string().nullable(),
  copyStatus: SequenceCopyStatusSchema.nullable(),
  blankAllowed: z.boolean(),
});
export type PlaybooksPlayOutlineStep = z.infer<typeof PlaybooksPlayOutlineStepSchema>;

/**
 * One distinct sequence the outline's ladder maps. `headVersionId` is the
 * value the copy save (`POST /plays/:id/sequences/:sequenceId/copy`) takes as
 * `expectedVersionId`. `settings` is read-only context from the identity
 * columns (`default_sender`, `default_cc`, `default_reply_to`, `flow_id`);
 * there is no settings write on the machine API (Decision 3). The value
 * spellings match `PlaybooksSequenceSummarySchema`'s `sender` / `cc` /
 * `replyTo` / `flowId` (free-form, `null` = not set).
 */
export const PlaybooksPlayOutlineSequenceSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  channel: LauncherChannelSchema,
  headVersionId: z.string().min(1),
  settings: z.object({
    sender: z.string().nullable(),
    cc: z.string().nullable(),
    replyTo: z.string().nullable(),
    flowId: z.string().min(1).nullable(),
  }),
  steps: z.array(PlaybooksPlayOutlineStepSchema),
});
export type PlaybooksPlayOutlineSequence = z.infer<typeof PlaybooksPlayOutlineSequenceSchema>;

/**
 * One rung of the outline's ladder, in rung order. `conditionsSummary` is
 * `describe-conditions.ts#describeConditions` of the segmentation head's
 * conditions (the same lines the app renders). `sequenceId` is the rung's
 * `mapping.sequenceId` (the play-owned id on a snapshotted play), `null` for an
 * unmapped rung.
 */
export const PlaybooksPlayOutlineBucketSchema = z.object({
  position: z.number().int().nonnegative(),
  rungId: z.string().min(1),
  segmentationId: z.string().min(1),
  name: z.string(),
  conditionsSummary: z.array(z.string()),
  isCatchAll: z.boolean(),
  sequenceId: z.string().min(1).nullable(),
});
export type PlaybooksPlayOutlineBucket = z.infer<typeof PlaybooksPlayOutlineBucketSchema>;

/**
 * `GET /plays/:id/outline` — the skill-facing read of a whole play in one call
 * (plan 2026-09-25-002, Key Technical Decision 18): the play and its direct
 * link, the ladder it would send (`playbookVersionId`: the pin, or the
 * playbook head for a legacy play), its buckets, every mapped sequence with
 * read-only settings and steps, and the `{{Token}}` vocabulary the copy save
 * accepts (`_shared/launcher-copy-tokens.ts#COPY_VARIABLE_FIELDS`). A stable
 * contract independent of the UI read shapes.
 */
export const PlaybooksPlayOutlineResponseSchema = z.object({
  playId: z.string().min(1),
  campaignSfdcId: z.string().min(1),
  playUrl: z.string().url(),
  playbook: z.object({ id: z.string().min(1), name: z.string() }),
  playbookVersionId: z.string().min(1),
  buckets: z.array(PlaybooksPlayOutlineBucketSchema),
  sequences: z.array(PlaybooksPlayOutlineSequenceSchema),
  tokens: z.array(z.object({ variable: z.string().min(1), label: z.string().min(1), object: z.string().min(1) })),
});
export type PlaybooksPlayOutlineResponse = z.infer<typeof PlaybooksPlayOutlineResponseSchema>;

/**
 * The row `versioning.ts#lockPlayRowForUpdate` reads with `SELECT "id",
 * "status", "archived_at", "playbook_id", "playbook_version_id" FROM
 * "playbooks_play" WHERE "id" = $1 FOR UPDATE` — a `$queryRaw` row is a trust
 * boundary, so it goes through this schema (team convention). A play-copy save
 * and a send both take this lock first, so they serialize on the play row and
 * the save's launched/archived re-check sees the send's `status = 'launched'`
 * (S5, play-scoped copy review). `playbook_id` / `playbook_version_id` feed
 * the snapshot a play-scoped write takes on a legacy play (`null` pin =
 * not snapshotted, plans/2026-09-25-002-playbooks-play-isolation-lean-plan.md
 * Key Technical Decision 4).
 */
export const PlaybooksPlayLockRowSchema = z.object({
  id: z.string(),
  status: z.string(),
  archived_at: z.date().nullable(),
  playbook_id: z.string(),
  playbook_version_id: z.string().nullable(),
});
export type PlaybooksPlayLockRow = z.infer<typeof PlaybooksPlayLockRowSchema>;

/**
 * The generation-relevant columns of a `launcher_sfdc_campaigns` row, as the
 * generate-copy handler's raw-SQL read returns them.
 *
 * A schema rather than a cast because a `$queryRaw` result is a trust boundary
 * (team convention "Schema-First Data Boundaries"). Deliberately narrower than
 * `PlaybooksCampaignMemberRowSchema`'s loose passthrough: this SELECT names
 * its columns explicitly, so there is no unknown-column tail to preserve.
 * `start_date`/`end_date` are `date` columns cast to text in the query — the
 * prompt wants the calendar date, never a timezone-shifted `Date`.
 */
export const PlaybooksGenerationCampaignRowSchema = z.object({
  name: z.string(),
  type: z.string().nullable(),
  subtype: z.string().nullable(),
  start_date: z.string().nullable(),
  end_date: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  description: z.string().nullable(),
  target_persona: z.string().nullable(),
  goal: z.string().nullable(),
  event_brief_url: z.string().nullable(),
});
export type PlaybooksGenerationCampaignRow = z.infer<
  typeof PlaybooksGenerationCampaignRowSchema
>;

/**
 * Round-3b QA fixes, G1 — the id+name pair `usagePlaces`' ONE raw-SQL name
 * lookup returns for `launcher_sfdc_campaigns` (same "$queryRaw row is a
 * trust boundary" rule as `PlaybooksGenerationCampaignRowSchema` above,
 * narrowed to just the two columns that lookup selects).
 */
export const PlaybooksCampaignNameRowSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type PlaybooksCampaignNameRow = z.infer<typeof PlaybooksCampaignNameRowSchema>;

/**
 * One row of the campaign's `cm_status` distribution. `member_count` is cast to
 * `int` in the query rather than left as Postgres `bigint`: Prisma hands a
 * bigint back as a JS `BigInt`, which is neither JSON-serializable nor
 * `z.number()`-parseable. A campaign roster is hundreds of rows, so `int` is
 * never in danger of overflowing.
 */
export const PlaybooksGenerationStatusCountRowSchema = z.object({
  cm_status: z.string().nullable(),
  member_count: z.number().int().nonnegative(),
});
export type PlaybooksGenerationStatusCountRow = z.infer<
  typeof PlaybooksGenerationStatusCountRowSchema
>;

/**
 * One `cm_status` value and how many of the campaign's members carry it —
 * the WIRE/PROMPT shape, downstream of the raw
 * {@link PlaybooksGenerationStatusCountRowSchema} DB row (whose `cm_status`
 * is nullable and whose column names are snake_case).
 *
 * Round-4 QA codex-review fix (wave 1, finding 6) — this is now the ONE
 * declaration of that shape. `copy-generation.ts`'s `RosterStatusCount` and
 * `copy-generation-rules.ts`'s `AudienceStatusCount` were two hand-written
 * interfaces for the same three facts as this schema's element, which is
 * exactly what `terra/no-parallel-schema-and-type` exists to prevent: both
 * now `z.infer` from here instead.
 */
export const PlaybooksRosterStatusCountSchema = z.object({
  status: z.string(),
  count: z.number().int().nonnegative(),
});
export type PlaybooksRosterStatusCount = z.infer<typeof PlaybooksRosterStatusCountSchema>;

/**
 * `GET /v2/sequences/:id/generation-context?campaignSfdcId=…` (U4b) — the
 * "Generate with AI" drawer's stage-1 seed data. Shares its two raw-SQL reads
 * (`readCampaignFacts`/`readRosterStatusCounts` in `./api.ts`) with the POST
 * `generate-copy` handler rather than re-querying — the two routes must never
 * see a different campaign snapshot.
 */
export const PlaybooksGenerationContextResponseSchema = z.object({
  /** camelCase campaign facts — same field set as `generate-copy`'s prompt inputs, plus `description`. */
  campaign: z.object({
    name: z.string(),
    type: z.string().nullable(),
    subtype: z.string().nullable(),
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    description: z.string().nullable(),
    targetPersona: z.string().nullable(),
    goal: z.string().nullable(),
    eventBriefUrl: z.string().nullable(),
  }),
  /** `readableEventName(campaign.name)` — falls back to the raw name if cleaning removes everything. */
  eventNameSeed: z.string(),
  rosterStatusCounts: z.array(PlaybooksRosterStatusCountSchema),
  /** `isThinDescription(campaign.description)`. */
  thinContext: z.boolean(),
  /** `DEFAULT_GENERATION_INSTRUCTION` — the same constant `generateSequenceCopy` falls back to. */
  defaultInstruction: z.string(),
  /** `playbooks_sequence.generation_preset`, `null` until a generation has ever happened for this sequence. */
  preset: PlaybooksGenerationPresetSchema.nullable(),
});
export type PlaybooksGenerationContextResponse = z.infer<
  typeof PlaybooksGenerationContextResponseSchema
>;

// ─────────────────────────────────────────────────────────────────────────────
// Matched members (PR B, U2)
//
// ── PR C CONTRACT FREEZE (plan Key Technical Decision 7) ─────────────────────
// `PlaybooksMatchedMembersResponseSchema` — `{ matchedMemberIds, sample,
// totalMatched }` — is the frozen per-rung / per-audience-rule shape. PR C's
// playbook-flow wiring and any future per-member routing display consume it
// AS-IS. Adding a field (e.g. a per-member "why matched") is additive and safe;
// renaming or dropping one of these three is a contract break.
//
// The ladder response below does not restate that shape — it SPREADS it into
// each rung entry (`...playbooksMatchedMembersFields`), so the two can never
// drift apart.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One `launcher_sfdc_campaign_members` row, as the campaign-members endpoints
 * put it on the wire.
 *
 * DELIBERATELY LOOSE. That table is raw-SQL with no Prisma model and ~60
 * columns that `src/services/launcher-member-sync.ts` keeps adding to; the v1
 * `GET /campaigns/:id/members` endpoint this mirrors does a bare `SELECT *` and
 * returns it untyped (`CampaignMember` in `mini-apps/launcher/components/types.ts`
 * is a hand-maintained interface, not a parse). Pinning all 60 columns here
 * would break on the next sync column and buy nothing; pinning the identity and
 * the columns `MembersPanel` actually reads — and passing the rest through
 * unchanged so the row stays shape-compatible with `fetchCampaignMembers` — is
 * the honest contract. Everything nullable because every one of these columns
 * is nullable in the migration.
 */
export const PlaybooksCampaignMemberRowSchema = z.looseObject({
  id: z.string().min(1),
  campaign_id: z.string().min(1),
  contact_name: z.string().nullable().optional(),
  contact_email: z.string().nullable().optional(),
  // `MembersPanel`'s Email column falls back to this for a Lead-sourced member
  // (no Contact row, so no `contact_email`) — named here for the same reason
  // the four above are: these are the columns that table actually reads.
  lead_email: z.string().nullable().optional(),
  cm_status: z.string().nullable().optional(),
  created_date: z.string().nullable().optional(),
});
export type PlaybooksCampaignMemberRow = z.infer<typeof PlaybooksCampaignMemberRowSchema>;

/**
 * Playbooks send PRD, U3 — the SEND-side read of the same
 * `launcher_sfdc_campaign_members` row.
 *
 * Same loose-passthrough rationale as `PlaybooksCampaignMemberRowSchema`
 * above (~60 raw-SQL columns the member sync keeps adding to, so pinning all
 * of them buys nothing), but a different NAMED set: this one names every
 * column the send path actually reads — `_shared`'s `buildTemplateVars` (the
 * 27-token vocabulary) plus the member fields launcher's `buildMemberPayload`
 * puts on the wire (`cm_status`, `notes__c`, the `*_first_name` fallbacks, the
 * scanner pair). A column dropped or renamed upstream therefore fails the
 * parse instead of silently emptying a `{{Token}}` in copy already on its way
 * to Clay.
 *
 * `id` and `campaign_id` are the only required columns: the payload keys
 * `campaign_member_id` off `id`, and the send is scoped to one campaign.
 * Everything else is `.nullable().optional()` because every one of these
 * columns is nullable in the migration and a narrower `SELECT` may not carry
 * it. `fit_score` is a `NUMERIC(5,1)` column — the caller normalizes Prisma's
 * `Decimal` to a JS number before parsing (see `matched-members-query.ts`'s
 * `normalizeValue`), which is why it is typed `z.number()` here.
 *
 * Its inferred type structurally satisfies `CampaignMember` once the
 * required-but-unselected keys are filled in — `send-payload.ts`'s
 * `toCampaignMember` adapter, not a cast.
 */
export const SendMemberRowSchema = z.looseObject({
  id: z.string().min(1),
  campaign_id: z.string().min(1),
  contact_id: z.string().nullable().optional(),
  contact_name: z.string().nullable().optional(),
  contact_email: z.string().nullable().optional(),
  contact_title: z.string().nullable().optional(),
  cm_status: z.string().nullable().optional(),
  notes__c: z.string().nullable().optional(),
  fit_score: z.number().nullable().optional(),
  owner_role: z.string().nullable().optional(),
  account_name: z.string().nullable().optional(),
  sales_tier: z.string().nullable().optional(),
  gtme_owner_name: z.string().nullable().optional(),
  gtme_owner_email: z.string().nullable().optional(),
  gtme_owner_first_name: z.string().nullable().optional(),
  gtme_meeting_link: z.string().nullable().optional(),
  gs_owner_name: z.string().nullable().optional(),
  gs_owner_email: z.string().nullable().optional(),
  gs_owner_first_name: z.string().nullable().optional(),
  gs_meeting_link: z.string().nullable().optional(),
  claydr_name: z.string().nullable().optional(),
  claydr_email: z.string().nullable().optional(),
  claydr_first_name: z.string().nullable().optional(),
  claydr_meeting_link: z.string().nullable().optional(),
  primary_met_with_name: z.string().nullable().optional(),
  primary_met_with_first_name: z.string().nullable().optional(),
  primary_met_with_email: z.string().nullable().optional(),
});
export type SendMemberRow = z.infer<typeof SendMemberRowSchema>;

/** How many `sample` rows a matched-member response carries. */
export const PLAYBOOKS_MATCHED_SAMPLE_SIZE = 10;

const playbooksMatchedMembersFields = {
  /** Every matched member id, in the query's `contact_name` order. */
  matchedMemberIds: z.array(z.string().min(1)),
  /** The first `PLAYBOOKS_MATCHED_SAMPLE_SIZE` matched rows, same order. */
  sample: z.array(PlaybooksCampaignMemberRowSchema),
  /** `matchedMemberIds.length` — carried explicitly so a count-only consumer needn't sum. */
  totalMatched: z.number().int().nonnegative(),
} as const;

/** Response of `GET`/`POST /v2/campaign-members/match` — the frozen shape. */
export const PlaybooksMatchedMembersResponseSchema = z.object(playbooksMatchedMembersFields);
export type PlaybooksMatchedMembersResponse = z.infer<
  typeof PlaybooksMatchedMembersResponseSchema
>;

/**
 * Body of `POST /v2/campaign-members/match` — the Audience Rule's UNSAVED draft
 * (Decision 3: preview only; persisting an audience rule is Phase 1). A POST
 * because the filter groups are a nested object, not because anything is
 * written: this route is read-only.
 */
export const PlaybooksMatchMembersRequestSchema = z.object({
  campaignSfdcId: z.string().min(1),
  groups: z.array(FilterGroupSchema),
  groupLogic: GroupLogicSchema,
});
export type PlaybooksMatchMembersRequest = z.infer<typeof PlaybooksMatchMembersRequestSchema>;

/**
 * One rung's slice of the ladder result. `rungId` is the rung's stable
 * `PlaybookRung.id` — the same key the Review step's approvals map and the
 * rung-scope store use, so a caller joins the ladder to a rendered rung by id
 * and never by position (a reorder rewrites every position, so a position-keyed
 * join can name one rung while reporting another's members).
 *
 * `position` rides along as informational ladder metadata (it is the order
 * first-match-wins was applied in) — it is not an identity. `segmentationId` is
 * carried so a caller can cross-check that the ladder and the rendered rung
 * still agree about which segmentation the rung points at.
 *
 * `isMapped` is `rung.mapping !== null`, carried so a caller can split the
 * member counts into the Review step's Routed / No-sequence buckets WITHOUT
 * refetching the playbook. Matching runs for an unmapped rung exactly as it does
 * for a mapped one — unmapped-ness changes who gets a send, not who matches.
 */
export const PlaybooksMatchedMembersRungSchema = z.object({
  rungId: z.string().min(1),
  position: z.number().int().nonnegative(),
  segmentationId: z.string().min(1),
  isCatchAll: z.boolean(),
  isMapped: z.boolean(),
  ...playbooksMatchedMembersFields,
});
export type PlaybooksMatchedMembersRung = z.infer<typeof PlaybooksMatchedMembersRungSchema>;

/** Response of `GET /v2/campaign-members/ladder` — the whole first-match-wins pass. */
export const PlaybooksMatchedMembersLadderResponseSchema = z.object({
  playbookId: z.string().min(1),
  playbookVersionId: z.string().min(1),
  /** In ladder (priority) order — the order first-match-wins was applied in. */
  rungs: z.array(PlaybooksMatchedMembersRungSchema),
  /** Members no rung claimed: with no catch-all, these route nowhere. */
  excludedMemberIds: z.array(z.string().min(1)),
  /** Every member on the campaign, matched or not. */
  totalMembers: z.number().int().nonnegative(),
});
export type PlaybooksMatchedMembersLadderResponse = z.infer<
  typeof PlaybooksMatchedMembersLadderResponseSchema
>;

// ─────────────────────────────────────────────────────────────────────────────
// Playbooks admin settings (`playbooks_settings`, playbooks-clay-wire-gaps
// plan, Unit 2) — `GET /v2/settings` / `PATCH /v2/settings`. Backs
// `./settings.ts`'s `readPlaybooksSettings`/`writePlaybooksSettings`, the
// DB-backed replacement for the `PLAYBOOKS_CLAY_ROUTINE_ID` env var.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `GET /v2/settings` response, and the shared echo shape `PATCH /v2/settings`
 * answers with too. Mirrors `./settings.ts#PlaybooksSettingsRow`'s camelCased
 * fields — all three `null` when no row has ever been written, so the admin
 * Settings page (Unit 3) renders an empty form rather than special-casing
 * "not found". `updatedAt` is the ISO-8601 wire form of the row's `Date`.
 */
export const PlaybooksSettingsResponseSchema = z.object({
  clayRoutineId: z.string().nullable(),
  updatedAt: z.string().nullable(),
  updatedByEmail: z.string().nullable(),
});
export type PlaybooksSettingsResponse = z.infer<typeof PlaybooksSettingsResponseSchema>;

/**
 * Body of `PATCH /v2/settings`. An explicit `null` clears the routine id
 * (back to the webhook transport); a non-null value must be a real,
 * non-blank, whitespace-free token. `.trim()` runs first, so a
 * whitespace-only string (`'   '`) fails the subsequent `.min(1)` and can
 * never be stored as "set" — same "blank collapses to null" contract
 * `writePlaybooksSettings` documents, enforced here so it never reaches the
 * DB in the first place. `.regex(/^\S+$/)` rejects an otherwise non-blank
 * value that still contains internal whitespace (e.g. `'has a space'`) — not
 * a shape any real Clay routine id or workflow token takes.
 */
export const PlaybooksUpdateSettingsRequestSchema = z.object({
  clayRoutineId: z.string().trim().min(1).max(256).regex(/^\S+$/).nullable(),
});
export type PlaybooksUpdateSettingsRequest = z.infer<typeof PlaybooksUpdateSettingsRequestSchema>;
